import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  lazy,
  Suspense,
} from "react";
import { processText } from "./engine/pipeline";
import {
  X,
  Share2,
  Download,
  ChevronLeft,
  ChevronRight,
  Check,
  LogIn,
  LogOut, // NEU: Logout Icon
  PenLine,
  Signature,
  Highlighter,
  Eraser,
} from "lucide-react";
import { Link } from "react-router-dom";
import CheckoutConsent from "./components/CheckoutConsent";
import { PLANS, CONTRACT_TERMS, getPlanByPriceId, TRIAL_PLAN } from "./plans";
import TrialOffer from "./components/TrialOffer";
import {
  COOKIE_DECIDED_EVENT,
  cookieBannerPending,
} from "./components/CookieConsent";
import { brand as ds, sx } from "./designSystem";

import { db } from "./firebase";
import {
  getAuth,
  signInWithPopup,
  signOut, // NEU: Für Logout
  GoogleAuthProvider,
  onAuthStateChanged,
  deleteUser,
} from "firebase/auth";
import {
  doc,
  updateDoc,
  increment,
  collection,
  addDoc,
  setDoc,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { ladeUndRegistriereFont, ladeStriche } from "./handschrift/fontStore";
import {
  berechneSchriftgroesse,
  berechnePaddingTop,
  berechneExportVersatz,
  messeGrundlinie,
} from "./schriftMetrik";
import { GN_PAPIERE, LINIE_FARBE, papierMasse } from "./papierVorlagen";
import {
  bereiteMatheVor,
  zerlegeInEinheiten,
  formelHTML,
  matheMetrik,
} from "./mathe";
import CoinIcon from "./games/CoinIcon";
import { COINS_PER_PDF, redeemCoins } from "./games/gamesFirebase";
import {
  ensureUserDocument,
  spendDownloadCredit,
  refundDownloadCredit,
} from "./credits";

// Der Editor zieht opentype.js nach (rund 55 KB gepackt). Das lohnt sich nur
// für die wenigen, die wirklich zeichnen — deshalb erst beim Öffnen laden.
// Das Anzeigen einer fertigen Schrift braucht das alles nicht: dafür genügt
// fontStore, und der kommt mit Firestore und der FontFace-Schnittstelle aus.
const HandschriftEditor = lazy(() => import("./handschrift/HandschriftEditor"));

// Pixel-Konfetti fürs "Münzen eingelöst"-Fenster: fallende Farbquadrate,
// bewusst kantig animiert (steps), passend zum Pixelart-Stil der Spiele.
const CONFETTI_COLORS = [
  "#f4b41a",
  "#ffdf6b",
  "#f05f96",
  "#37d67a",
  "#4aa5ff",
  "#ffffff",
];
const CONFETTI_PIECES = Array.from({ length: 28 }, (_, i) => ({
  left: (i * 37) % 100,
  size: 6 + ((i * 13) % 3) * 3,
  delay: ((i * 53) % 20) / 10,
  duration: 2.2 + ((i * 29) % 14) / 10,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
}));

const PixelConfetti = () => (
  <div
    className="pointer-events-none absolute inset-0 overflow-hidden"
    aria-hidden="true"
  >
    {CONFETTI_PIECES.map((p, i) => (
      <span
        key={i}
        className="pixel-confetti"
        style={{
          left: `${p.left}%`,
          width: p.size,
          height: p.size,
          backgroundColor: p.color,
          animationDelay: `${p.delay}s`,
          animationDuration: `${p.duration}s`,
        }}
      />
    ))}
  </div>
);

const glyphSupportCache = {};
const BACKEND_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "https://schriftbot-backend.vercel.app";

const STAR_PATH =
  "M12 2.5l2.94 6.1 6.56.9-4.8 4.6 1.2 6.6L12 17.5l-5.9 3.2 1.2-6.6-4.8-4.6 6.56-.9L12 2.5z";
/* Bis zu welcher x-Position der Stern gefüllt wird, damit n Zehntel seiner
   FLÄCHE farbig sind (Index = n). Nach Breite geschnitten wirkt ein Stern
   mit 0,3 fast leer, weil links nur die schmale Zacke liegt. */
const STAR_CLIP_X = [
  2.5, 7.1, 8.5, 9.8, 11.0, 12.0, 13.0, 14.2, 15.5, 16.9, 21.5,
];

/* Durchschnitt und Anzahl der Bewertungen unter dem Titel. Die Zahlen kommen
   vom Backend (/ratings-summary), weil die Feedback-Dokumente selbst nicht
   öffentlich lesbar sind. Klappt der Abruf nicht, bleibt die Zeile einfach
   weg — lieber nichts zeigen als eine erfundene Zahl. */
const RatingSummary = () => {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_BASE_URL}/ratings-summary`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.count > 0) setSummary(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!summary) return null;

  // Sterne und Zahl zeigen denselben gerundeten Wert, sonst sähe 4,25 als
  // „4,3“ mit einem Viertelstern aus.
  const rounded = Math.round(summary.average * 10) / 10;
  const average = rounded.toLocaleString("de-DE", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const label = `${average} von 5 Sternen aus ${summary.count.toLocaleString(
    "de-DE"
  )} Bewertungen`;

  return (
    <div
      className="flex items-center gap-2"
      role="img"
      aria-label={label}
      title={label}
    >
      <span className="flex" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => {
          // Zehntel dieses Sterns, z. B. 4,3 → vier volle und 3/10 vom fünften
          const tenths = Math.round(Math.max(0, Math.min(1, rounded - i)) * 10);
          return (
            <svg key={i} width="16" height="16" viewBox="0 0 24 24">
              <defs>
                <clipPath id={`sb-star-clip-${i}`}>
                  <rect x="0" y="0" width={STAR_CLIP_X[tenths]} height="24" />
                </clipPath>
              </defs>
              <path d={STAR_PATH} fill={ds.color.iconGray} />
              <path
                d={STAR_PATH}
                fill={ds.color.accent}
                clipPath={`url(#sb-star-clip-${i})`}
              />
            </svg>
          );
        })}
      </span>
      <span
        style={{
          fontFamily: ds.font.body,
          fontSize: 14,
          fontVariantNumeric: "tabular-nums",
          color: ds.color.muted,
          whiteSpace: "nowrap",
        }}
      >
        <strong style={{ color: ds.color.ink, fontWeight: 600 }}>
          {average}
        </strong>{" "}
        aus {summary.count.toLocaleString("de-DE")} Bewertungen
      </span>
    </div>
  );
};
const MAX_TEXT_LENGTH = 5000;

/* Erlaubte Zeichen im Eingabefeld: deutsche Tastatur, Umlaute, §, °, µ, €,
   Anführungszeichen, Auslassungspunkte — und die Mathe-Zeichen, die der
   Formelsatz zeichnen kann (griechische Buchstaben, Hoch-/Tiefzahlen, Pfeile,
   ≤ ≥ ≠ ≈ √ π, ℝ ℕ ℤ ℚ, ⅓ …). Vorher fielen die hier schon beim Tippen weg. */
const bereinigeEingabe = (wert) =>
  wert.replace(/[^\x20-\x7E\xA0-\xFF\n\r€‘-‟–-—…‹›™Ͱ-Ͽ′⁰-₟℀-⅟←-⇿∀-⋿△]/g, "");

function isCharSupportedByFont(char, exactFontName) {
  // Whitespace und spezielle Zeichen IMMER erlauben
  if (
    char === " " ||
    char === "\n" ||
    char === "\r" ||
    char === "\t" ||
    char === "¥"
  )
    return true;

  // Zahlen IMMER erlauben (0-9)
  if (/[0-9]/.test(char)) return true;

  // Basis-ASCII-Sonderzeichen IMMER erlauben (.,!? usw.)
  if (/[!\"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~§°µ€]/.test(char)) return true;

  // Deutsche Umlaute & ß IMMER erlauben
  if (/[äöüßÄÖÜ]/.test(char)) return true;

  // Font-Laden-Check: Wenn Font noch nicht geladen, NICHT rausfiltern
  const fontFaceSet = document.fonts;
  if (fontFaceSet && typeof fontFaceSet.check === "function") {
    const isLoaded = fontFaceSet.check(`16px "${exactFontName}"`);
    if (!isLoaded) return true; // Font lädt noch → char erlauben
  }

  const cacheKey = `${exactFontName}_${char}`;
  if (glyphSupportCache[cacheKey] !== undefined)
    return glyphSupportCache[cacheKey];

  // Nur für exotische Zeichen: Messung durchführen
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const testSize = "30px";

  ctx.font = `${testSize} "${exactFontName}", monospace`;
  const widthMono = ctx.measureText(char).width;

  ctx.font = `${testSize} "${exactFontName}", sans-serif`;
  const widthSans = ctx.measureText(char).width;

  let isSupported = widthMono === widthSans;

  if (isSupported) {
    ctx.font = `${testSize} "${exactFontName}"`;
    const tofuWidth = ctx.measureText("\uFFFF").width;
    if (widthMono === tofuWidth && widthMono > 0) isSupported = false;
  }

  glyphSupportCache[cacheKey] = isSupported;
  return isSupported;
}
/* Ein beschriftetes Bedienelement. Auf Modul-Ebene, damit die Eingaben
   beim Tippen nicht neu eingehängt werden. Der optionale `value` steht
   rechts neben dem Label — dort, wo vorher der Wert in die Beschriftung
   hineingeschrieben wurde ("Schriftgröße (100%)"). */
const Field = ({ label, value, children }) => (
  <div className="flex flex-col gap-2.5">
    <div className="flex items-baseline justify-between gap-3">
      <label style={sx.sub}>{label}</label>
      {value && (
        <span
          style={{
            fontFamily: ds.font.body,
            fontSize: 14,
            fontWeight: 500,
            fontVariantNumeric: "tabular-nums",
            color: ds.color.muted,
          }}
        >
          {value}
        </span>
      )}
    </div>
    {children}
  </div>
);

export default function Home() {
  const [humanizeEnabled, setHumanizeEnabled] = useState(false);
  const [originalText, setOriginalText] = useState("");
  const auth = getAuth();
  const [user, setUser] = useState(null);
  const [dbCredits, setDbCredits] = useState(0);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [hasPREMIUM, setHasPREMIUM] = useState(false);
  const [currentPlan, setCurrentPlan] = useState("");
  // Erst wenn klar ist, ob und welches Abo jemand hat, darf das
  // Testangebot erscheinen — sonst blitzt es kurz bei Abonnenten auf.
  const [abostatusBekannt, setAbostatusBekannt] = useState(false);
  const [testphaseGenutzt, setTestphaseGenutzt] = useState(false);
  const [gameCoins, setGameCoins] = useState(0);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemedCredits, setRedeemedCredits] = useState(null);

  // "Münzen einlösen" bei Schriftbot: wandelt volle 10er in Downloads um und
  // zeigt NUR hier (Konfetti-Fenster) an, wie viele freigeschaltet wurden.
  const handleRedeemCoins = async () => {
    if (redeeming) return;
    setRedeeming(true);
    const result = await redeemCoins();
    setRedeeming(false);
    if (result && result.redeemedCredits > 0) {
      setRedeemedCredits(result.redeemedCredits);
    }
  };
  const [ownHandwritingAllowed, setOwnHandwritingAllowed] = useState(false);

  // Im Browser gezeichnete Handschrift. Sie liegt in Firestore, nicht im
  // Bundle — deshalb steht sie in keiner fonts.css und in keinem switch,
  // sondern wird zur Laufzeit als FontFace registriert. Der State hält die
  // uid, sobald das geklappt hat; das ist zugleich der Familienname.
  const [eigeneSchriftUid, setEigeneSchriftUid] = useState(null);
  const [zeigeSchriftEditor, setZeigeSchriftEditor] = useState(false);

  useEffect(() => {
    if (!user) {
      setEigeneSchriftUid(null);
      return undefined;
    }
    let abgebrochen = false;
    (async () => {
      const bereit = await ladeUndRegistriereFont(user.uid);
      if (!abgebrochen && bereit) setEigeneSchriftUid(user.uid);
    })();
    return () => {
      abgebrochen = true;
    };
  }, [user]);
  const [renewalDate, setRenewalDate] = useState(null); // NEU

  const [messiness, setMessiness] = useState(20); // 0 to 100

  /* Strichstärke der eigenen Handschrift, 0 = so gezeichnet.

     Umgesetzt über `-webkit-text-stroke`, nicht über einen Neubau der Schrift.
     Die Strichstärke steckt zwar in den Glyphen, aber sie nachträglich dort zu
     ändern hiesse: Striche aus Firestore holen, opentype.js laden, achtzig
     Zeichen neu vektorisieren — für jeden Ruck am Regler. Der Umriss legt sich
     stattdessen um die vorhandenen Buchstaben und macht sie dicker, ohne die
     Vorbreiten anzufassen. Zeilenumbruch und Seitenaufteilung bleiben deshalb
     unberührt.

     Für den Download zählt das genauso: html2canvas liest
     `-webkit-text-stroke` aus und zeichnet den Umriss mit (strokeText in
     dessen Renderer). Vorschau und Datei sehen also gleich aus. */
  const [strichStaerke, setStrichStaerke] = useState(0);
  const MAX_STRICH_STAERKE_PX = 1.6;
  const [lineHeight, setLineHeight] = useState(35); // Standardwert 35px

  const PLAN_LEVELS = {
    "": 0, // Kein Abo
    expired: 0, // Kein Abo
    Basic: 1, // Basic (20 Downloads)
    Pass: 2, // Pro (100 Downloads)
    Unlimited: 3, // Unlimited
    Go: 4,
    400: 5,
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) setAbostatusBekannt(true);
      if (currentUser) {
        const userRef = doc(db, "users", currentUser.uid);
        const unsubscribeDoc = onSnapshot(userRef, (docSnap) => {
          setAbostatusBekannt(true);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setDbCredits(data.credits || 0);
            setIsUnlimited(data.isUnlimited || false);
            setTestphaseGenutzt(Boolean(data.trialStartedAt));
            setCurrentPlan(data.plan || "");
            setGameCoins(data.gameCoins || 0);
            // Auch zurücknehmen: Endet das Abo (z. B. Kündigung in der
            // Testphase), ist die eigene Handschrift sofort wieder zu.
            setOwnHandwritingAllowed(
              data.plan === "Go" ||
                data.plan === "Unlimited" ||
                data.plan === "400"
            );
            setHasPREMIUM(data.hasPREMIUM || false);

            // KORRIGIERT: payments Array aus dem User-Dokument auslesen
            const paymentsArray = data.payments;

            console.log("Payments Array:", paymentsArray);

            if (
              paymentsArray &&
              Array.isArray(paymentsArray) &&
              paymentsArray.length > 0
            ) {
              // Finde das Payment mit dem neuesten Datum
              const latestPayment = paymentsArray.reduce((latest, current) => {
                const currentDate = new Date(current.date);
                const latestDate = new Date(latest.date);
                return currentDate > latestDate ? current : latest;
              });

              console.log("Latest Payment:", latestPayment);

              if (latestPayment.date) {
                const paymentDate = new Date(latestPayment.date);
                const renewal = new Date(paymentDate);
                renewal.setMonth(renewal.getMonth() + 1); // +1 Monat

                console.log("Payment Date:", paymentDate);
                console.log("Renewal Date:", renewal);
                setRenewalDate(renewal);
              }
            } else {
              console.log("No payments found in array");
              setRenewalDate(null);
            }
          } else {
            // Kein Firestore-Schreibzugriff beim Laden – das Dokument wird
            // beim Login (handleLogin) angelegt. Hier nur lokalen State setzen.
            setDbCredits(0);
            setIsUnlimited(false);
            setCurrentPlan("");
            setGameCoins(0);
            setHasPREMIUM(false);
            setRenewalDate(null);
          }
        });

        return () => {
          unsubscribeDoc();
        };
      }
    });

    return () => unsubscribeAuth();
  }, [auth]);
  // 3. Google Login

  // 4. Credit abziehen
  // Die Abbuchung liegt in credits.js und läuft im Backend als Transaktion
  // GEGEN DEN SERVERSTAND, nicht gegen `dbCredits` aus dem lokalen State.
  // Das war vorher ein increment(-1) auf gut Glück: zwei Tabs oder ein
  // schneller Doppelklick konnten mit einem Guthaben zwei Downloads ziehen,
  // und der Stand konnte ins Minus laufen. Jetzt wird VOR der Erzeugung
  // abgebucht (siehe downloadAsPDF/downloadAsImage) und bei einem Fehlschlag
  // zurückgebucht.

  const totalCredits = user ? dbCredits : 0;
  // ENDE
  // JETZT STRIPE:

  // --- LOGIN LOGIK ---
  // ANGEPASSTE LOGIN FUNKTION
  const [toast, setToast] = useState(null);
  const [isRewriting, setIsRewriting] = useState(false);

  // Bestellübersicht: der Tarif, der gerade bestätigt wird (null = zu).
  const [pendingPlan, setPendingPlan] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  // Konto-Löschung: eigener Dialog statt window.confirm.
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);

  // 2. Toast-Funktion
  const showToast = (message, type = "success", dauer = 4000) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), dauer); // Toast standardmäßig nach 4s ausblenden
  };

  // 3. Login-Funktion anpassen
  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const loggedInUser = result.user;

      // Konto anlegen bzw. auffrischen und die Gratis-Downloads beim Server
      // abholen. Wie viele es gibt — und ob überhaupt — entscheidet allein
      // das Backend; siehe credits.js.
      const account = await ensureUserDocument(loggedInUser);

      if (account?.granted > 0) {
        showToast(
          `${account.granted} Downloads liegen für dich bereit.`,
          "success"
        );
      } else if (account?.isNewUser && account?.reason === "throttled") {
        // Von diesem Anschluss kamen heute schon mehrere neue Konten. Das
        // Konto funktioniert normal, nur die Gutschrift wartet.
        showToast(
          "Die Gratis-Downloads sind hier heute schon vergeben — morgen wieder, oder direkt Münzen in den Spielen sammeln.",
          "success"
        );
      }
      return loggedInUser;
    } catch (error) {
      console.error("Login Fehler:", error);
      showToast("❌ LOGIN FEHLGESCHLAGEN", "error");
      return null;
    }
  };

  // Kein Rückfrage-Dialog: Ausloggen ist mit einem Klick rückgängig zu machen.
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setDbCredits(0);
      showToast("Du bist abgemeldet.", "success");
    } catch (error) {
      console.error("Logout Fehler", error);
      showToast("Abmelden hat nicht geklappt.", "error");
    }
  };

  // Löschen bleibt bestätigungspflichtig — es ist nicht umkehrbar. Die
  // Rückfrage läuft aber über den eigenen Dialog (showDeleteAccount),
  // nicht über einen Browser-Alert.
  const handleDeleteAccount = async () => {
    if (!user) return;
    setShowDeleteAccount(false);

    try {
      // 1. Backend aufrufen: Stripe Abo kündigen & Firestore löschen.
      // Die Identität wird über das Firebase-ID-Token nachgewiesen.
      const idToken = await user.getIdToken();
      const response = await fetch(`${BACKEND_BASE_URL}/delete-user-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) throw new Error("Backend konnte Daten nicht löschen.");

      // 2. Erst wenn Stripe/Firestore sauber sind, den Auth-User löschen
      await deleteUser(user);

      showToast("Dein Konto und dein Abo wurden gelöscht.", "success");
      setUser(null);
      setDbCredits(0);
      setIsUnlimited(false);
    } catch (error) {
      console.error("Fehler beim Löschen:", error);

      if (error.code === "auth/requires-recent-login") {
        showToast(
          "Bitte melde dich neu an und versuche es direkt nochmal.",
          "error"
        );
        await auth.signOut();
      } else {
        showToast("Löschen fehlgeschlagen. Bitte nochmal versuchen.", "error");
      }
    }
  };

  // Öffnet die Bestellübersicht (Preis, Laufzeit, Kündigung, Einwilligungen).
  // Der Checkout selbst startet erst aus dieser Ansicht heraus — siehe
  // components/CheckoutConsent.jsx.
  const subscribeToPlan = async (
    priceId,
    currentUser,
    consent,
    trial = false
  ) => {
    const activeUser = currentUser || user;

    if (!activeUser) {
      showToast("Bitte melde dich zuerst an.", "error");
      return;
    }
    // Ohne den Hinweis auf AGB, Datenschutz und Widerruf aus der
    // Bestellübersicht kein Checkout.
    if (!consent?.terms) {
      showToast("Bitte öffne die Bestellübersicht erneut.", "error");
      return;
    }

    setCheckoutBusy(true);
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: activeUser.uid,
          email: activeUser.email,
          priceId: priceId,
          // Nachweis: exakter Wortlaut + Version + Zeitpunkt des Hinweises.
          consent,
          trial,
        }),
      });

      // Testphase schon einmal genutzt (oder schon ein Abo): kein Fehler,
      // sondern ein ehrlicher Hinweis — und ab jetzt kein Angebot mehr.
      if (trial && res.status === 409) {
        setTestphaseGenutzt(true);
        setPendingPlan(null);
        setCheckoutBusy(false);
        showToast("Du hast die Testphase schon einmal genutzt.", "error");
        return;
      }

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Server Fehler:", errorText);
        throw new Error(`Server antwortet mit ${res.status}`);
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Die Bezahlseite konnte nicht geöffnet werden.");
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      showToast(
        "Bezahlseite nicht erreichbar. Bitte nochmal versuchen.",
        "error"
      );
      setCheckoutBusy(false);
    }
  };

  const handleRewriteWithAI = async () => {
    const inputText = text.trim();
    if (!inputText) {
      showToast("Schreib zuerst etwas in das Textfeld.", "error");
      return;
    }

    setIsRewriting(true);
    try {
      const response = await fetch(`${BACKEND_BASE_URL}/rewrite-text`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: inputText }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Serverfehler (${response.status})`);
      }

      const rewrittenText =
        typeof data.rewrittenText === "string" ? data.rewrittenText : "";
      if (!rewrittenText.trim()) {
        throw new Error("Die KI hat keinen Text zurückgegeben.");
      }

      const normalizedText = rewrittenText.slice(0, MAX_TEXT_LENGTH);
      setText(normalizedText);
      if (humanizeEnabled) {
        setOriginalText(normalizedText);
      }
    } catch (error) {
      console.error("Fehler beim KI-Umschreiben:", error);
      showToast("Umschreiben hat nicht geklappt. Versuch es nochmal.", "error");
    } finally {
      setIsRewriting(false);
    }
  };

  // Login prüfen, dann die Bestellübersicht öffnen. Gekauft wird erst dort.
  const handleBuyProcess = async (priceId, { trial = false } = {}) => {
    const plan = trial ? TRIAL_PLAN : getPlanByPriceId(priceId);
    if (!plan) {
      console.error("Unbekannte priceId:", priceId);
      showToast("Dieser Tarif ist gerade nicht verfügbar.", "error");
      return;
    }

    try {
      let activeUser = user;
      if (!activeUser) {
        activeUser = await handleLogin();
        if (!activeUser) return; // handleLogin meldet den Abbruch selbst
      }
      setCheckoutBusy(false);
      setPendingPlan(plan);
    } catch (error) {
      console.error("Fehler in handleBuyProcess:", error);
      showToast("Da ging etwas schief. Bitte nochmal versuchen.", "error");
    }
  };

  //ENDE STRIPE

  const [text, setText] = useState("");
  const [debouncedText, setDebouncedText] = useState("");

  const [font, setFont] = useState("Sebastian");
  const [fontSizeScale, setFontSizeScale] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [variationSeed, setVariationSeed] = useState(0);
  const [isDownloadMode, setIsDownloadMode] = useState(false);

  const [previewScale, setPreviewScale] = useState(1);
  const wrapperRef = useRef(null);
  const [paperColor, setPaperColor] = useState("#ffffff");
  const [textColor, setTextColor] = useState("#000000");
  const [lineStyle, setLineStyle] = useState("lines");
  const pageRefs = useRef([]);
  const drawingDataRef = useRef({});
  const [drawingTool, setDrawingTool] = useState(null); // null = no tool selected
  const [isPaginating, setIsPaginating] = useState(false);

  /* Tipp-Animation beim ersten Text: die Buchstaben erscheinen einer nach
     dem anderen, als würden sie in Windeseile getippt. Nur einmal pro
     Besuch — bei jeder weiteren Änderung steht der Text wie gehabt sofort
     da. Solange sie läuft, steht hier { start, schritt } (ms pro Zeichen). */
  const [tippen, setTippen] = useState(null);
  const tippenGezeigtRef = useRef(false);

  const [generatedImages, setGeneratedImages] = useState([]);
  const [showImageGallery, setShowImageGallery] = useState(false);

  const [showHandschriftModal, setShowHandschriftModal] = useState(false);

  const handleMeineHandschriftClick = () => {
    setShowHandschriftModal(true);
  };

  // Cache für die letzte Berechnung
  // Cache für die letzte Berechnung
  const lastCalculationRef = useRef({
    text: "",
    pages: [],
    fontUsed: "",
    fontSizeScale: 1,
  }); // <-- fontUsed hinzugefügt

  /* Zielwert für die x-Höhe der im Browser gezeichneten Handschrift.

     berechneSchriftgroesse normiert jede Schrift auf dieselbe x-Höhe. Der
     Standardwert 0,256 ist an Sebastian geeicht und stimmt für die
     mitgelieferten Schriften.

     Rechnerisch gilt er auch für die gezeichnete Schrift — sie wirkt damit aber
     kleiner. Der Grund: die x-Höhe allein entscheidet nicht darüber, wie gross
     Schrift aussieht. Eine gezeichnete Schrift entsteht an Hilfslinien mit
     x-Höhe 500 und Oberlänge 800 von 1000 Einheiten, ihre Oberlängen sind also
     nur das 1,6-fache der x-Höhe und ihr Geviert entsprechend klein. Bei
     gleicher x-Höhe steht damit weniger Buchstabe auf dem Papier.

     DAS IST DER STELLHEBEL für die Grösse der eigenen Handschrift:
     grösser = grössere Schrift. Er wirkt ausschliesslich auf die gezeichnete
     Schrift; die TTF-Schriften benutzen weiter den Standardwert aus
     schriftMetrik.js und bleiben davon unberührt. */
  const ZIEL_X_HOEHE_EIGENE = 0.375; // Standard der TTF-Schriften: 0,256

  /* Schriftgrösse aus den echten Metriken der Schrift, nicht aus einer Liste.
     Vorher stand hier eine Tabelle mit von Hand gepflegten Werten; die x-Höhe
     schwankte dadurch zwischen den Schriften um fast Faktor zwei, und jede neue
     Schrift bekam blind 50. Siehe schriftMetrik.js. */
  const getBaseFontSize = (fontName) =>
    fontName && fontName === eigeneSchriftUid
      ? berechneSchriftgroesse(
          getFontFamily(fontName),
          LINE_SPACING,
          ZIEL_X_HOEHE_EIGENE
        )
      : berechneSchriftgroesse(getFontFamily(fontName), LINE_SPACING);

  const sketchRefsByPage = useRef({});
  const activeDrawingKeyRef = useRef(null);
  const persistAllDrawings = async () => {
    console.log("Refs vorhanden:", Object.keys(sketchRefsByPage.current));
    const entries = Object.entries(sketchRefsByPage.current);

    await Promise.all(
      entries.map(async ([key, ref]) => {
        // ✅ ref ist SCHON ein Ref-Object (mit .current), also direkt verwenden
        const api = ref?.current;
        if (!api) return;
        try {
          const paths = await api.exportPaths();
          drawingDataRef.current[key] = paths;
          console.log(`✅ Gespeichert: ${key}`);
        } catch (e) {
          console.error(`❌ Fehler bei ${key}:`, e);
        }
      })
    );
  };

  const getActiveCanvasApi = () => {
    const activeKey = activeDrawingKeyRef.current;
    if (activeKey && sketchRefsByPage.current[activeKey]?.current) {
      return sketchRefsByPage.current[activeKey].current;
    }

    const firstAvailableRef = Object.values(sketchRefsByPage.current).find(
      (ref) => ref?.current
    );
    return firstAvailableRef?.current || null;
  };

  const undoDrawing = () => {
    getActiveCanvasApi()?.undo();
  };

  const redoDrawing = () => {
    getActiveCanvasApi()?.redo();
  };

  const [showAIModal, setShowAIModal] = useState(false);
  const [aiQuery, setAiQuery] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiContextText, setAiContextText] = useState("");

  const handleAIChat = async () => {
    if (!aiQuery.trim()) return;

    setAiLoading(true);
    setAiResponse("");

    const payload = aiContextText
      ? `Vorheriger Text:\n${aiContextText}\n\nNeue Anweisung: ${aiQuery}`
      : aiQuery;

    try {
      const response = await fetch(`${BACKEND_BASE_URL}/rewrite-text`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: payload }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || `Serverfehler (${response.status})`);
      }

      const result =
        typeof data.rewrittenText === "string"
          ? data.rewrittenText
          : "Keine Antwort erhalten";

      setAiContextText("");
      setAiResponse(result);
    } catch (error) {
      console.error("AI Fehler:", error);
      setAiResponse(`Fehler: ${error.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAIKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !aiLoading) {
      e.preventDefault();
      if (!isUnlimited) {
        setShowAIModal(false);
        resetAIChat();
        zeigeAngebotOderPreise();
      } else {
        handleAIChat();
      }
    }
  };

  const resetAIChat = () => {
    setAiQuery("");
    setAiResponse("");
    setAiContextText("");
  };

  // Konstanten
  const MIN_FONT_SCALE_PERCENT = 80;
  const MAX_FONT_SCALE_PERCENT = 120;
  const FONT_SCALE_STEP_PERCENT = 1;
  const FONT_SIZE = 50;
  const A4_WIDTH = 794;
  const A4_HEIGHT = 1123;
  const MARGIN = 60;
  const MAX_TEXT_HEIGHT = A4_HEIGHT - MARGIN * 2 - 30;

  /* Eine der GoodNotes-Vorlagen (papierVorlagen.js) gewählt? Dann richten
     sich Zeilenabstand, Schriftgrösse und Laufweite nach deren Linien. Bei
     Linien, Kariert und Blanko ist gnPapier null, und alle Werte unten sind
     genau die alten. */
  const gnPapier = GN_PAPIERE[lineStyle]
    ? papierMasse(lineStyle, MARGIN)
    : null;
  const LINE_SPACING = gnPapier ? gnPapier.zeile : 35;
  const TEXT_LINKS =
    gnPapier && gnPapier.textLinks ? gnPapier.textLinks : MARGIN;
  const TEXT_BREITE = A4_WIDTH - TEXT_LINKS - MARGIN;

  /* useLayoutEffect, nicht useEffect: sonst malt der Browser den fertigen
     Text einmal, bevor die Animation ihn wieder ausblendet. */
  useLayoutEffect(() => {
    if (tippenGezeigtRef.current || !text.trim()) return;
    tippenGezeigtRef.current = true;
    // Höchstens ~1,8 s, egal wie lang der Text ist
    const schritt = Math.min(20, 1800 / text.length);
    setTippen({ start: Date.now(), schritt });
    setTimeout(() => setTippen(null), text.length * schritt + 300);
  }, [text]);

  // Debounce
  useEffect(() => {
    if (text !== debouncedText) {
      setIsPaginating(true);
      const timer = setTimeout(() => {
        setDebouncedText(text);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [text, debouncedText]);

  /* Der Text, so wie er gesetzt wird: Formeln (3/4, x^2, $…$, \frac{…}{…})
     sind darin markiert und für die Seitenaufteilung je ein unteilbares Wort.
     Siehe mathe/normalisieren.js. Ohne Formeln ist er identisch mit dem
     eingegebenen Text. */
  const satzText = useMemo(
    () => bereiteMatheVor(debouncedText),
    [debouncedText]
  );

  // Random Helpers
  function seededRandom(seed) {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  }

  const charVariationCache = useRef({});

  function getCharVariation(index) {
    const cacheKey = `${variationSeed}_${index}`;

    if (!charVariationCache.current[cacheKey]) {
      const seed = variationSeed + index;
      const sizeVariation = (seededRandom(seed * 1.1) - 0.8) * 0.15;
      const spacingVariation = (seededRandom(seed * 2.3) - 5) / 1;
      const yOffset = seededRandom(seed * 3.7) - 0.7;

      charVariationCache.current[cacheKey] = {
        sizeVariation,
        spacingVariation,
        yOffset,
      };
    }

    return charVariationCache.current[cacheKey];
  }

  // Clear cache when variationSeed changes
  useEffect(() => {
    charVariationCache.current = {};
  }, [variationSeed]);

  /* Ist gerade die im Browser gezeichnete Handschrift eingestellt?

     Sie ist keine TTF-Datei aus dem Bundle, sondern eine zur Laufzeit aus den
     Strichen gebaute OTF (handschrift/buildFont.js). Die Unterscheidung läuft
     genauso wie in getFontFamily: dort hat die gezeichnete Schrift Vorrang vor
     einer gleichnamigen Bundle-Datei, und beides muss dieselbe Schrift meinen.
     Ein Bestandsnutzer, dessen Handschrift als TTF mitgeliefert wird, fällt
     hier also nicht hinein und wird wie jede andere TTF behandelt. */
  const istEigeneHandschrift = !!eigeneSchriftUid && font === eigeneSchriftUid;

  /* Laufweite eines Zeichens.

     Die feste Pixelzahl unten ist an die mitgelieferten Schriften geeicht: die
     haben durchweg rund 50 Einheiten Seitenabstand links und rechts, zusammen
     also 0,1 Geviert. Bei Sebastian mit seinen 50 px Schriftgrösse sind das
     genau die 5 px, die hier wieder abgezogen werden — die Buchstaben berühren
     sich dadurch, und genau davon lebt das Schriftbild.

     Die gezeichnete Handschrift hat mit 2 x 45 Einheiten (METRIK.seitenAbstand)
     praktisch denselben Seitenabstand — aber eine viel kleinere Schriftgrösse,
     weil ihre x-Höhe ein halbes Geviert misst statt eines Fünftels. Bei rund
     18 px sind 0,09 Geviert nur noch 1,6 px; die festen 5 px reissen dann das
     Dreifache heraus und die Buchstaben schieben sich übereinander. Für sie
     wird die Laufweite deshalb an die Schriftgrösse gekoppelt.

     Für die TTF-Schriften bleibt der Wert unverändert. */
  const LAUFWEITE_REFERENZ_GROESSE = 50;
  const berechneLaufweite = (variation, fontsize) => {
    const fest = -0.5 + variation.spacingVariation;
    if (!istEigeneHandschrift) return gnPapier ? fest * gnPapier.faktor : fest;
    return fest * (fontsize / LAUFWEITE_REFERENZ_GROESSE);
  };

  const generateTokenHTMLWithTransform = (
    token,
    startIndex,
    currentFontSize
  ) => {
    let html = "";
    for (let i = 0; i < token.length; i++) {
      const char = token[i];
      const variation = getCharVariation(startIndex + i);
      const isSpace = char === " ";
      const isNewline = char === "\n";

      // NEU: Zeichen auch bei der Höhen/Breiten-Messung überspringen!
      if (!isSpace && !isNewline && !isCharSupportedByFont(char, font)) {
        continue;
      }

      const fontSize = currentFontSize * (1 + variation.sizeVariation);
      const letterSpacing = berechneLaufweite(variation, currentFontSize);
      const translateY = variation.yOffset;

      if (isNewline) {
        html += `<span style="display:block;width:100%;height:${LINE_SPACING}px;"></span>`;
      } else {
        html += `<span style="display:inline-block; line-height:0px; font-size:${fontSize}px;letter-spacing:${letterSpacing}px;transform:translateY(${translateY}px);${
          isSpace ? "width:0.3em;" : ""
        }">${isSpace ? " " : char}</span>`;
      }
    }
    return html;
  };

  const getFontSpecificSettings = (fontName, sizeScale) => {
    const baseSize = getBaseFontSize(fontName);
    const scaledSize = baseSize * sizeScale;

    const referenceLineHeight = LINE_SPACING; // Dynamischer Wert statt Fixwert

    /* Oberer Abstand so, dass die Grundlinie der ersten Zeile auf einer
       Papierlinie sitzt — bei jeder Schrift auf derselben. Der Wert war früher
       fest (29 px in der Vorschau, ein anderer im Export); damit landete jede
       Schrift woanders, weil jede ihre eigene Grundlinienlage mitbringt.

       Die Linien beginnen 20 px unter dem Seitenrand, der Textkasten sitzt am
       Seitenrand — daher die 20. Vorschau und Export rechnen bewusst gleich:
       der Export klont den Vorschaukasten, jeder Unterschied schlägt dort
       unmittelbar durch. */
    let ERSTE_LINIE_VOM_KASTEN = gnPapier ? gnPapier.ersteLinieVomKasten : 20;

    /* Bei den GoodNotes-Vorlagen kann die erste Startlinie für Schriften mit
       hohen Oberlängen zu knapp sein (Moritz auf Kariert groß): dann würde der
       obere Abstand auf 0 gekappt und der Text sässe unter der Linie. In dem
       Fall eine Papierlinie tiefer beginnen. */
    if (gnPapier) {
      const grundlinie = messeGrundlinie(
        getFontFamily(fontName),
        scaledSize,
        referenceLineHeight
      );
      while (ERSTE_LINIE_VOM_KASTEN + referenceLineHeight < grundlinie) {
        ERSTE_LINIE_VOM_KASTEN += gnPapier.raster;
      }
    }

    const basePaddingTop = berechnePaddingTop(
      getFontFamily(fontName),
      scaledSize,
      referenceLineHeight,
      ERSTE_LINIE_VOM_KASTEN,
      referenceLineHeight
    );

    /* Beim Export sitzt der Text tiefer als in der Vorschau, obwohl beide
       dasselbe Markup benutzen: html2canvas zeichnet Text nicht über den
       Zeilenkasten, sondern mit `fillText` auf eine selbst gemessene
       Grundlinie — und diese Messung fällt hier deutlich zu tief aus.
       Warum genau, steht ausführlich in schriftMetrik.js.

       Entscheidend ist: der Fehler wächst mit der Schriftgrösse. Deshalb kann
       hier keine feste Zahl stehen. Vorher stand hier eine (40 px), und weil
       die Schriftgrössen zwischen 21 px (Anton) und 76 px (Moritz) liegen,
       stimmte sie für keine Schrift wirklich — je nach Schrift sass der Text
       im Export mal über, mal auf der Linie.

       Ausgeglichen wird über `top` statt über den oberen Abstand: `top`
       verschiebt nur die Darstellung und lässt Zeilenumbruch und
       Seitenaufteilung unberührt. Und nur im Export — die Vorschau bleibt
       unverändert, dort sitzt der Text schon richtig. */

    /* Gemessen werden muss mit der Grösse, in der die Zeichen tatsächlich
       gesetzt werden, nicht mit der nominellen. getCharVariation verkleinert
       jedes Zeichen um (seededRandom() - 0,8) · 0,15; da seededRandom
       gleichverteilt in [0;1) liegt, ist der Mittelwert (0,5 - 0,8) · 0,15,
       also -0,045. Bleibt es beim Mittelwert, verteilt sich der Restfehler
       gleichmässig um null. */
    const MITTLERE_GROESSENVARIATION = -0.045;

    /* Die im Browser gezeichnete Handschrift hatte hier eine Ausnahme mit einem
       festen Wert von 40 px. Der stammt noch aus der Zeit, als auch die
       TTF-Schriften eine feste Zahl hatten, und war für sie um ein Vielfaches zu
       gross: bei rund 18 px Schriftgrösse braucht sie nur etwa 11 px Ausgleich,
       sass im Download also fast eine ganze Zeile zu hoch.

       Die Ausnahme ist deshalb weg. Für die TTF-Schriften ändert sich dadurch
       nichts — sie durchlaufen exakt denselben Aufruf wie vorher. */
    const exportVersatz = berechneExportVersatz(
      getFontFamily(fontName),
      scaledSize * (1 + MITTLERE_GROESSENVARIATION),
      referenceLineHeight
    );
    const marginOffset = isDownloadMode ? -exportVersatz : 0;

    return {
      newlineHeight: referenceLineHeight,
      paddingTop: `${basePaddingTop}px`,
      marginTop: `${marginOffset}px`,
      fontsize: scaledSize,
      ersteLinieVomKasten: ERSTE_LINIE_VOM_KASTEN,
    };
  };

  // Font mit Backup, kein Fallback zu System-Fonts
  const getFontFamily = (fontName) => {
    // Eine im Browser gezeichnete Handschrift kommt aus Firestore und ist zur
    // Laufzeit als FontFace registriert. Sie steht deshalb vor dem switch —
    // wer selbst gezeichnet hat, soll das auch sehen, selbst wenn für dieselbe
    // uid noch eine ältere mitgelieferte Schriftdatei im Bundle liegt.
    // Die Backup-Schrift bleibt dahinter stehen: fehlende Zeichen (etwa nicht
    // gezeichnete Satzzeichen) fallen automatisch auf sie zurück.
    if (fontName && fontName === eigeneSchriftUid) {
      return `"${fontName}", "SebastianBackup"`;
    }

    switch (fontName) {
      case "Sebastian":
        return '"Sebastian", "SebastianBackup"';
      case "Leonie":
        return '"Leonie", "LeonieBackup"';
      case "Moritz":
        return '"Moritz", "MoritzBackup"';
      case "Amelie":
        return '"Amelie", "AmelieBackup"';
      case "Lisa":
        return '"Lisa", "LisaBackup"';
      case "Max":
        return '"Max", "MaxBackup"';
      case "Paula":
        return '"Paula", "SebastianBackup"';
      case "Marie":
        return '"Marie", "AmelieBackup"';
      case "Alex":
        return '"Alex", "MoritzBackup"';
      case "Theo":
        return '"Theo", "MoritzBackup"';
      case "Anton":
        return '"Anton", "SebastianBackup"';
      case "Luis":
        return '"Luis", "SebastianBackup"';
      case "Melissa":
        return '"Melissa", "AmelieBackup"';
      case "Maya":
        return '"Maya", "LisaBackup"';
      case "Tom":
        return '"Tom", "LisaBackup"';
      case "Noah":
        return '"Noah", "LisaBackup"';
      case "Robin":
        return '"Robin", "SebastianBackup"';
      case "Druckschrift":
        return '"Druckschrift", "SebastianBackup"';
      case "eb9X3wVKSFX806LnS0HZ4MM5gDW2":
        return '"eb9X3wVKSFX806LnS0HZ4MM5gDW2", "MoritzBackup"';
      case "cPZZqjFcnSOac7XzwAkSyL8JgAn1":
        return '"cPZZqjFcnSOac7XzwAkSyL8JgAn1", "SebastianBackup"';
      case "Vb1mfb02ERg6V0ihJmVlxIPlxh83":
        return '"Vb1mfb02ERg6V0ihJmVlxIPlxh83", "SebastianBackup"';
      case "D8x1nm6nD3RBx3rLEISPiQBxbfF3":
        return '"D8x1nm6nD3RBx3rLEISPiQBxbfF3", "SebastianBackup"';
      case "qVlqjhBOZ3OaPiCi9kjT3bRVq3S2":
        return '"qVlqjhBOZ3OaPiCi9kjT3bRVq3S2", "SebastianBackup"';
      case "gmLD2QBEVBSAGGv2TKSNSoadYps2":
        return '"gmLD2QBEVBSAGGv2TKSNSoadYps2", "SebastianBackup"';
      case "UE9UNirRXvQeR10vHApr4dXE0GC3":
        return '"UE9UNirRXvQeR10vHApr4dXE0GC3", "SebastianBackup"';
      case "igKTbUebLWYu3hKs7ryGt1B173W2":
        return '"igKTbUebLWYu3hKs7ryGt1B173W2", "SebastianBackup"';
      case "FFkTKZsTukddex07krfJaX8vn603":
        return '"FFkTKZsTukddex07krfJaX8vn603", "SebastianBackup"';
      case "8VwqZIzHnfaQ1jjen8qK1myKYEh1":
        return '"8VwqZIzHnfaQ1jjen8qK1myKYEh1", "SebastianBackup"';
      case "21fAIbnhowV0AWkm9BliIFQlJnu1":
        return '"21fAIbnhowV0AWkm9BliIFQlJnu1", "SebastianBackup"';
      case "yR1IXAtxVPOaLLIgwVNPeajBSb12":
        return '"yR1IXAtxVPOaLLIgwVNPeajBSb12", "SebastianBackup"';

      case "rLrXApn2r7cVoHwFWqckefEsAze2":
        return '"rLrXApn2r7cVoHwFWqckefEsAze2", "SebastianBackup"';
      case "NwuIpCWFsBb31IruDU0x485oeNB3":
        return '"NwuIpCWFsBb31IruDU0x485oeNB3", "SebastianBackup"';

      case "pZZJxBfz7LdEhTjEPcCQ299qLHj2":
        return '"pZZJxBfz7LdEhTjEPcCQ299qLHj2", "SebastianBackup"';
      case "Qhc6L449nKawF1RkuRsbytpJmKj1":
        return '"Qhc6L449nKawF1RkuRsbytpJmKj1", "SebastianBackup"';
      case "eAivGELfe3cYfvOxavVzKyfgJaK2":
        return '"eAivGELfe3cYfvOxavVzKyfgJaK2", "SebastianBackup"';

      case "34WJ6Y4EfTUnzu5ffM8KSR5ADI93":
        return '"34WJ6Y4EfTUnzu5ffM8KSR5ADI93", "SebastianBackup"';

      case "VUEgOEQAS3Or9nKquqcizaLTRdo2":
        return '"VUEgOEQAS3Or9nKquqcizaLTRdo2", "SebastianBackup"';

      default:
        return '"Sebastian", "SebastianBackup"';
    }
  };

  /* Muss nach getFontFamily stehen: die Grössenberechnung schlägt die Schrift
     dort nach. */
  const fontSettings = getFontSpecificSettings(font, fontSizeScale);

  /* Breite des Umrisses in Pixeln. Nur für die eigene Handschrift, sonst 0 —
     damit ist ausgeschlossen, dass der Regler je eine TTF-Schrift verändert.
     Mitskaliert mit der Schriftgrösse, sonst wirkt derselbe Wert bei 26 px
     Schrift dreimal so fett wie bei 76 px. */
  const umrissBreite =
    user && font === user.uid
      ? strichStaerke * MAX_STRICH_STAERKE_PX * (fontSettings.fontsize / 50)
      : 0;

  /* Einstellungen für den Formelsatz (mathe/darstellen.js). Dieselben Werte wie
     für die normalen Zeichen, damit eine Formel aussieht, als stünde sie in
     derselben Zeile mit demselben Stift geschrieben.

     Strichstärke: bei der gezeichneten Handschrift kennt man sie genau
     (METRIK.strichBreite plus Umriss-Regler), bei den TTF-Schriften wird sie
     am Bindestrich der Schrift abgenommen. */
  const matheOptionen = (startIndex) => {
    const stapel = getFontFamily(font);
    const S = fontSettings.fontsize;
    const dicke = istEigeneHandschrift
      ? 0.062 * S + umrissBreite
      : matheMetrik(stapel).dicke * S;
    const exportVersatzBasis = isDownloadMode
      ? -parseFloat(fontSettings.marginTop) || 0
      : 0;
    return {
      stapel,
      S,
      dicke,
      farbe: textColor,
      unordnung: messiness,
      laufweite: (s) => berechneLaufweite({ spacingVariation: -4.5 }, s),
      variation: getCharVariation,
      index: startIndex,
      zeile: {
        L: LINE_SPACING,
        a: messeGrundlinie(stapel, S, LINE_SPACING),
      },
      export: isDownloadMode
        ? {
            V0: exportVersatzBasis,
            S0: S * (1 - 0.045),
          }
        : null,
    };
  };

  const [wordSpacing, setWordSpacing] = useState("normal");
  // Füge diesen State zu deinen anderen hinzu (z. B. unter const [font, setFont] ...)
  const [fontLoadedCounter, setFontLoadedCounter] = useState(0);

  // Dieser Effect wartet, bis der neue Font fertig geladen ist, und erzwingt dann ein exaktes Re-Rendering
  useEffect(() => {
    let cancelled = false;

    setIsPaginating(true);

    // Cache für Glyph-Support leeren, sonst bleiben alte Messungen hängen
    // (anderer FontName => anderer Key, aber sicherheitshalber sauber halten)
    // eslint-disable-next-line no-unused-vars
    for (const k in glyphSupportCache) delete glyphSupportCache[k];

    (async () => {
      try {
        // Font gezielt laden (wichtig: fonts.ready heißt nur "alle bisherigen loads", nicht zwingend der neue)
        if (document.fonts && document.fonts.load) {
          await document.fonts.load(`16px ${getFontFamily(font)}`);
          /* Auch die Ersatzschrift jetzt laden: + − = ( ) < > stehen nur
             dort, und der Formelsatz vermisst sie, bevor sie sonst irgendwo
             auf der Seite vorkämen. */
          await Promise.all(
            getFontFamily(font)
              .split(",")
              .map((f) =>
                document.fonts
                  .load(`16px ${f.trim()}`, "+-=()<>/%~|.")
                  .catch(() => null)
              )
          );
        }
        await document.fonts.ready;

        if (cancelled) return;

        // Layout tick
        setTimeout(() => {
          if (!cancelled) setFontLoadedCounter((prev) => prev + 1);
        }, 50);
      } catch (e) {
        // Im Fehlerfall trotzdem rerendern, damit Text nicht hängen bleibt
        if (!cancelled) setFontLoadedCounter((prev) => prev + 1);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [font]);

  // --- DIE INTELLIGENTE PAGINIERUNG (OPTIMIERT) ---
  const pages = useMemo(() => {
    if (!satzText) {
      setTimeout(() => setIsPaginating(false), 0);
      lastCalculationRef.current = {
        text: "",
        pages: [],
        fontUsed: font,
        variationSeed: variationSeed,
        fontSizeScale: fontSizeScale,
        fontLoadedCounter: fontLoadedCounter,
      };
      return [];
    }

    const lastCalc = lastCalculationRef.current;
    let reusedPages = [];
    let startGlobalIndex = 0;

    // Cache-Check: Font & Seed müssen gleich sein
    //
    // fontLoadedCounter gehört mit in den Vergleich. Beim Umschalten auf eine
    // Schrift, die noch nicht geladen ist, läuft dieser Block nämlich zweimal:
    // einmal sofort — da misst der Browser noch mit der Ersatzschrift und
    // liefert falsche Buchstabenbreiten — und einmal, wenn die Schrift da ist.
    // Ohne den Zähler sähe der zweite Durchlauf nur "gleiche Schrift wie beim
    // letzten Mal" und übernähme die falsch umbrochenen Seiten. Sichtbar wurde
    // das als Seite, die mitten im Satz endet.
    const isFontOrSeedChanged =
      lastCalc.fontUsed !== font ||
      lastCalc.variationSeed !== variationSeed ||
      lastCalc.fontSizeScale !== fontSizeScale ||
      lastCalc.wordSpacing !== wordSpacing ||
      lastCalc.fontLoadedCounter !== fontLoadedCounter ||
      lastCalc.satzBreite !== TEXT_BREITE ||
      lastCalc.zeile !== LINE_SPACING;

    if (!isFontOrSeedChanged && lastCalc.pages.length > 0) {
      for (let i = 0; i < lastCalc.pages.length; i++) {
        const page = lastCalc.pages[i];

        // PERFORMANCE FIX:
        // Wir brechen nur ab, wenn es die letzte Seite ist UND sie NICHT voll war.
        // War die Seite 'full', können wir sie wiederverwenden, auch wenn danach neuer Text kommt.
        if (i === lastCalc.pages.length - 1 && !page.isFull) break;

        // Prüfen, ob der Text an dieser Stelle noch exakt übereinstimmt
        // (startsWith ist viel schneller als slice bei langen Texten)
        const matches = satzText.startsWith(page.text, startGlobalIndex);

        if (matches) {
          // Prüfen auf sauberen Übergang (Wortende oder Textende)
          const nextCharIndex = startGlobalIndex + page.text.length;
          const nextChar = satzText[nextCharIndex];

          const isCleanBreak =
            nextChar === undefined || // Text zu Ende
            /\s/.test(nextChar) || // Leerzeichen/Newline folgt
            page.isFull; // Seite war eh voll (harter Cut war erlaubt)

          if (isCleanBreak) {
            reusedPages.push(page);
            startGlobalIndex += page.text.length;
          } else {
            break;
          }
        } else {
          break; // Text hat sich geändert
        }
      }
    } else {
      // WICHTIG: Wenn sich Font ändert, müssen wir bei 0 anfangen!
      lastCalculationRef.current.pages = [];
      reusedPages = [];
      startGlobalIndex = 0;
    }

    // Nur den neuen Teil berechnen
    const textToCalculate = satzText.slice(startGlobalIndex);

    /* Bei den GoodNotes-Vorlagen endet die Seite an der letzten Papierlinie
       (höchstens 20 px über dem unteren Rand): so viele Zeilen, wie dort
       Grundlinien hinpassen. Bei den alten Papieren gilt MAX_TEXT_HEIGHT wie
       bisher. */
    let maxSatzHoehe = MAX_TEXT_HEIGHT;
    if (gnPapier) {
      const ersteLinie = MARGIN + fontSettings.ersteLinieVomKasten;
      const letzteLinie = Math.max(
        ...gnPapier.waagrecht.map((l) => l.y).filter((y) => y <= A4_HEIGHT - 20)
      );
      const zeilen = Math.floor(
        (letzteLinie - ersteLinie) / LINE_SPACING + 1e-6
      );
      maxSatzHoehe =
        parseFloat(fontSettings.paddingTop) + (zeilen + 0.5) * LINE_SPACING;
    }

    // Wenn alles gecached wurde und kein neuer Text da ist -> Fertig
    if (reusedPages.length > 0 && textToCalculate.length === 0) {
      setTimeout(() => setIsPaginating(false), 0);
      return reusedPages;
    }
    // Setup Mess-Container
    const tempDiv = document.createElement("div");
    tempDiv.style.cssText = `
    position: absolute;
    visibility: hidden;
    width: ${TEXT_BREITE}px;
    font-family: ${getFontFamily(font)};
    font-size: ${fontSettings.fontsize}px;
    line-height: ${LINE_SPACING}px;
    white-space: pre-wrap; 
    word-wrap: break-word; 
    left: -9999px;
    top: -9999px;
    padding-top: ${fontSettings.paddingTop};
    margin-top: ${fontSettings.marginTop};
  `;
    document.body.appendChild(tempDiv);

    // *****************************************************************
    // ** HILFSFUNKTION FÜR DIE MESSUNG DES NEWLINE-VERHALTENS **
    // *****************************************************************
    const renderTokenInTempDiv = (token) => {
      // NEUER TOKEN MIT ¥-PLATZHALTER (wie in A4Page)
      const modifiedToken = token.replace(
        /((?:\n){2,})/g,
        (match) =>
          Array(match.length - 1)
            .fill("¥")
            .join("") + "\n"
      );

      // Token sicher auftrennen, damit ¥ nicht an Wörtern klebt
      const subTokens = modifiedToken.split(/([ \t]+|\n+|¥+)/).filter(Boolean);

      subTokens.forEach((subToken) => {
        let processToken = subToken;
        if (wordSpacing === "large" && /^[ \t]+$/.test(subToken)) {
          processToken = subToken.replace(/ /g, "  ");
        }

        zerlegeInEinheiten(processToken).forEach((einheit) => {
          // Eine Formel wird als Ganzes vermessen — genau so, wie A4Page
          // sie setzt, damit Umbruch und Seitenende übereinstimmen.
          if (einheit.mathe) {
            const formelSpan = document.createElement("span");
            formelSpan.innerHTML = formelHTML(einheit.quelle, matheOptionen(0));
            tempDiv.appendChild(formelSpan);
            return;
          }
          const char = einheit.ch;
          const isNewline = char === "\n";
          const isFullLineBreak = char === "¥";

          let heightStyle = "";
          if (isNewline) {
            heightStyle = "height: 0px;";
          } else if (isFullLineBreak) {
            heightStyle = `height: ${LINE_SPACING}px;`;
          }

          const charSpan = document.createElement("span");

          if (isNewline || isFullLineBreak) {
            charSpan.style.cssText = `
                    display: block;
                    width: 100%;
                    ${heightStyle}
                `;
            charSpan.textContent = " ";
          } else {
            charSpan.innerHTML = generateTokenHTMLWithTransform(
              char,
              0,
              fontSettings.fontsize
            );
          }
          tempDiv.appendChild(charSpan);
        });
      });
    };
    // *****************************************************************

    /* Für die GoodNotes-Vorlagen: genau so messen, wie A4Page setzt. Die
       Messung oben gibt jedem Zeichen die Variation von Zeichen 0 (kleinste
       Grösse, engste Laufweite) und bricht auch mitten im Wort um; sie schätzt
       die Zeilen also zu knapp. Bei den alten Papieren bleibt das so, dort
       reicht der Platz unten. Bei den Vorlagen soll die letzte Zeile auf der
       letzten Linie stehen, deshalb hier mit denselben Zeichen-Indizes,
       Wort-Kästen und Leerzeichen wie in A4Page. seitenIndex zählt dabei wie
       globalCharIndex dort. */
    let seitenIndex = startGlobalIndex;
    const renderTokenWieA4Page = (token) => {
      const modifiedToken = token.replace(
        /((?:\n){2,})/g,
        (match) =>
          Array(match.length - 1)
            .fill("¥")
            .join("") + "\n"
      );
      modifiedToken
        .split(/([ \t]+|\n+|¥+)/)
        .filter(Boolean)
        .forEach((subToken) => {
          if (subToken.includes("\n") || subToken.includes("¥")) {
            for (const ch of subToken) {
              const umbruch = document.createElement("span");
              umbruch.style.cssText = `display:block;width:100%;height:${
                ch === "¥" ? LINE_SPACING : 0
              }px;`;
              umbruch.textContent = " ";
              tempDiv.appendChild(umbruch);
              seitenIndex++;
            }
            return;
          }
          if (/^[ \t]+$/.test(subToken)) {
            const leer = document.createElement("span");
            leer.style.whiteSpace = "pre-wrap";
            leer.textContent =
              wordSpacing === "large" ? subToken.replace(/ /g, "  ") : subToken;
            tempDiv.appendChild(leer);
            seitenIndex++;
            return;
          }
          const wort = document.createElement("span");
          wort.style.cssText = "display:inline-block;white-space:nowrap;";
          for (const einheit of zerlegeInEinheiten(subToken)) {
            const teil = document.createElement("span");
            teil.innerHTML = einheit.mathe
              ? formelHTML(einheit.quelle, matheOptionen(seitenIndex))
              : generateTokenHTMLWithTransform(
                  einheit.ch,
                  seitenIndex,
                  fontSettings.fontsize
                );
            wort.appendChild(teil);
            seitenIndex += einheit.laenge;
          }
          tempDiv.appendChild(wort);
        });
    };
    const renderToken = gnPapier ? renderTokenWieA4Page : renderTokenInTempDiv;

    // Wir teilen nur den Rest-Text auf
    const tokens = textToCalculate.split(/(\s+)/);
    const newPages = [];

    let currentPageText = "";
    let currentPageStartIdx = startGlobalIndex;
    let currentTokenIdx = startGlobalIndex;

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      if (token.length === 0) continue;

      // ****************************************************
      // ** NEUE MESSLOGIK: RENDER TOKEN MIT NEWLINE-FIX **
      // ****************************************************
      const currentTokenChildrenCount = tempDiv.children.length;
      renderToken(token); // Führt die Messung im tempDiv durch

      // Check overflow
      if (tempDiv.offsetHeight > maxSatzHoehe) {
        // Rollback: Alle Elemente entfernen, die durch diesen Token hinzugefügt wurden
        const addedChildren =
          tempDiv.children.length - currentTokenChildrenCount;
        for (let k = 0; k < addedChildren; k++) {
          tempDiv.removeChild(tempDiv.lastChild);
        }

        newPages.push({
          text: currentPageText,
          startIndex: currentPageStartIdx,
          isFull: true,
        });

        // Reset für nächste Seite
        tempDiv.innerHTML = "";
        seitenIndex = currentTokenIdx;
        renderToken(token); // Den Token, der nicht mehr passte, auf die neue Seite

        currentPageStartIdx = currentTokenIdx;
        currentPageText = token;
      } else {
        currentPageText += token;
      }

      currentTokenIdx += token.length;
    }

    // Den Rest als letzte (nicht volle) Seite speichern
    if (currentPageText.length > 0) {
      newPages.push({
        text: currentPageText,
        startIndex: currentPageStartIdx,
        isFull: false, // Die letzte Seite ist meistens nicht "randvoll"
      });
    }

    document.body.removeChild(tempDiv);

    const finalPages = [...reusedPages, ...newPages];

    lastCalculationRef.current = {
      text: satzText,
      pages: finalPages,
      fontUsed: font,
      variationSeed: variationSeed,
      fontSizeScale: fontSizeScale,
      wordSpacing: wordSpacing,
      fontLoadedCounter: fontLoadedCounter,
      satzBreite: TEXT_BREITE,
      zeile: LINE_SPACING,
    };

    // ... restlicher Code in useMemo für pages ...

    setTimeout(() => setIsPaginating(false), 0);
    return finalPages;
  }, [
    satzText,
    font,
    variationSeed,
    A4_WIDTH,
    MARGIN,
    FONT_SIZE,
    LINE_SPACING,
    TEXT_BREITE,
    MAX_TEXT_HEIGHT,
    fontSettings.fontsize,
    fontSettings.newlineHeight,
    fontSettings.paddingTop,
    fontLoadedCounter,
    wordSpacing,
    fontSizeScale,
    /* Die Laufweite hängt daran (siehe berechneLaufweite), und die Laufweite
       entscheidet über den Zeilenumbruch. Für die TTF-Schriften ist der Wert
       konstant false — dort ändert sich dadurch nichts. */
    istEigeneHandschrift,
  ]);

  // Update refs
  pageRefs.current = pages.map(
    (_, i) => pageRefs.current[i] || { current: null }
  );

  // Linien
  const lines = [];
  const totalLines = Math.floor((A4_HEIGHT - MARGIN) / LINE_SPACING) - 2;
  for (let i = 0; i < totalLines; i++) {
    lines.push(i);
  }

  // Update refs
  pageRefs.current = pages.map(
    (_, i) => pageRefs.current[i] || { current: null }
  );

  const [brushColor, setBrushColor] = useState("#000000"); // Standard: Schwarz
  const [brushSize, setBrushSize] = useState(4); // Standard: 4px
  // Logik für Stift vs. Marker
  const isEraser = drawingTool === "eraser";
  const isMarker = drawingTool === "marker";

  // Wenn Marker: Farbe 40% transparent machen (Hex 66) und Dicke vervierfachen
  const currentStrokeColor = isMarker ? `${brushColor}66` : brushColor;
  const currentStrokeWidth = isMarker ? brushSize * 5 : brushSize;
  // A4 Seite Komponente
  // A4 Seite Komponente
  const A4Page = ({
    pageText,
    pageIndex,
    startIndex,
    messiness,
    wordSpacing,
    lineHeight,
  }) => {
    const drawingKey = useMemo(() => `page_${pageIndex}`, [pageIndex]);
    const sketchCanvasRef = useRef(null);
    const isHydratingDrawingRef = useRef(true);

    useEffect(() => {
      sketchRefsByPage.current[drawingKey] = sketchCanvasRef;
      return () => {
        delete sketchRefsByPage.current[drawingKey];
      };
    }, [drawingKey]);

    useEffect(() => {
      let cancelled = false;
      isHydratingDrawingRef.current = true;

      const restore = async () => {
        const api = sketchCanvasRef.current;
        if (!api) return;

        const savedPaths = drawingDataRef.current[drawingKey];
        console.log(
          `[${drawingKey}] Restore versucht, gespeicherte Paths:`,
          savedPaths?.length ?? 0
        );

        if (!savedPaths || savedPaths.length === 0) {
          console.log(`[${drawingKey}] Keine Paths zum Laden.`);
          requestAnimationFrame(() => {
            if (!cancelled) isHydratingDrawingRef.current = false;
          });
          return;
        }

        try {
          await api.clearCanvas();
          console.log(`[${drawingKey}] Canvas geleert.`);
        } catch (e) {
          console.error(`[${drawingKey}] Clear failed:`, e);
        }

        requestAnimationFrame(() => {
          if (cancelled) return;
          try {
            api.loadPaths(savedPaths);
            console.log(`[${drawingKey}] ✅ Paths geladen!`);
          } catch (e) {
            console.error(`[${drawingKey}] Load failed:`, e);
          }
          isHydratingDrawingRef.current = false;
        });
      };

      restore();

      return () => {
        cancelled = true;
      };
    }, [drawingKey, drawingTool, currentStrokeColor, currentStrokeWidth]);

    const persistPaths = async () => {
      if (!sketchCanvasRef.current) return;
      try {
        const paths = await sketchCanvasRef.current.exportPaths();
        drawingDataRef.current[drawingKey] = paths;
      } catch (_) {
        // noop
      }
    };

    const handleCanvasChange = (updatedPaths) => {
      if (isHydratingDrawingRef.current) return;
      drawingDataRef.current[drawingKey] = updatedPaths;
      activeDrawingKeyRef.current = drawingKey;
    };

    // Undo / Redo mit Tastenkürzel (STRG+Z / CMD+Z)
    useEffect(() => {
      const onKeyDown = (e) => {
        const isMac = navigator.platform.toLowerCase().includes("mac");
        const mod = isMac ? e.metaKey : e.ctrlKey;
        if (!mod) return;

        if (e.key.toLowerCase() === "z") {
          e.preventDefault();
          if (e.shiftKey) {
            sketchCanvasRef.current?.redo();
          } else {
            sketchCanvasRef.current?.undo();
          }
        }
      };

      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const gridWidth = A4_WIDTH;
    const gridStartY = MARGIN + 20;
    const gridKaestchen = LINE_SPACING / 2;
    const gridObenY =
      gridStartY - Math.floor(gridStartY / gridKaestchen) * gridKaestchen;
    const gridZeilen = Math.floor((A4_HEIGHT - gridObenY) / gridKaestchen) + 1;

    /* Zeitversatz je Zeichen für die Tipp-Animation. Gerechnet ab dem Start,
       nicht ab dem Zeichnen der Seite: die Seite wird zwischendurch neu
       aufgebaut, und schon getippte Buchstaben sollen dabei stehen bleiben. */
    const tippStil = (index) =>
      tippen && !isDownloadMode
        ? {
            animation: "sbTipp 80ms ease-out both",
            animationDelay: `${
              index * tippen.schritt - (Date.now() - tippen.start)
            }ms`,
          }
        : null;

    const seededRandomLocal = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };

    return (
      <div
        style={{
          height: `${A4_HEIGHT * previewScale}px`,
          width: `${A4_WIDTH * previewScale}px`,
        }}
      >
        <div
          ref={(el) => (pageRefs.current[pageIndex] = { current: el })}
          style={{
            width: `${A4_WIDTH}px`,
            height: `${A4_HEIGHT}px`,
            padding:
              TEXT_LINKS === MARGIN
                ? `${MARGIN}px`
                : `${MARGIN}px ${MARGIN}px ${MARGIN}px ${TEXT_LINKS}px`,
            transformOrigin: "top left",
            transform: `scale(${previewScale})`,
            backgroundColor: paperColor,
          }}
          className="box-border relative shadow-xl"
        >
          {/* HINTERGRUND LINIEN / KÄSTCHEN */}
          {gnPapier ? (
            <>
              {/* GoodNotes-Vorlage: Linien an denselben Stellen wie im PDF,
                  siehe papierVorlagen.js. Wie bei den alten Papieren genau
                  1 px dick und auf ganze Pixel gelegt: mit der PDF-Dicke von
                  0,67 px rundete der Browser je nach Lage mal auf 1, mal auf
                  2 px, und die Linien wirkten unterschiedlich dick. */}
              {gnPapier.waagrecht.map((l, i) => (
                <div
                  key={`gh-${i}`}
                  style={{
                    position: "absolute",
                    left: `${Math.round(l.x0)}px`,
                    width: `${Math.round(l.x1) - Math.round(l.x0)}px`,
                    top: `${Math.round(l.y - 0.5)}px`,
                    height: "1px",
                    background: LINIE_FARBE,
                  }}
                />
              ))}
              {gnPapier.senkrecht.map((l, i) => (
                <div
                  key={`gv-${i}`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: `${Math.round(l.x - 0.5)}px`,
                    width: "1px",
                    height: `${A4_HEIGHT}px`,
                    background: l.farbe,
                  }}
                />
              ))}
            </>
          ) : lineStyle === "lines" ? (
            lines.map((i) => (
              <div
                key={i}
                style={{
                  left: 0,
                  width: gridWidth,
                  top: `${gridStartY + i * LINE_SPACING}px`,
                }}
                className="absolute h-px bg-gray-300"
              />
            ))
          ) : lineStyle === "grid" ? (
            <>
              {/* Kästchen über die ganze Seite: im selben Raster wie die
                  Schreiblinien, nur nach oben und unten bis zum Rand fortgesetzt */}
              {Array.from({ length: gridZeilen }).map((_, i) => (
                <div
                  key={`h-${i}`}
                  style={{
                    left: 0,
                    width: gridWidth,
                    top: `${gridObenY + i * (LINE_SPACING / 2)}px`,
                  }}
                  className="absolute h-px bg-gray-300"
                />
              ))}

              {Array.from({
                length: Math.floor(gridWidth / (LINE_SPACING / 2)),
              }).map((_, i) => (
                <div
                  key={`v-${i}`}
                  style={{
                    left: `${LINE_SPACING / 2 + i * (LINE_SPACING / 2)}px`,
                    top: 0,
                    height: `${A4_HEIGHT}px`,
                  }}
                  className="absolute w-px bg-gray-300"
                />
              ))}
            </>
          ) : null}

          <div
            style={{
              fontFamily: getFontFamily(font),
              fontSize: `${fontSettings.fontsize}px`,
              lineHeight: `${LINE_SPACING}px`, // Starr auf 35px
              paddingTop: fontSettings.paddingTop,

              // ÄNDERUNG: Nutze top anstatt marginTop für html2canvas!
              marginTop: 0,
              top: fontSettings.marginTop,

              color: textColor,
              /* Strichstärke der eigenen Handschrift. Bei 0 — und bei jeder
                 anderen Schrift — steht hier "0px", dann zeichnet weder der
                 Browser noch html2canvas einen Umriss. Die TTF-Schriften sind
                 davon also nicht berührt. */
              WebkitTextStrokeWidth: `${umrissBreite}px`,
              WebkitTextStrokeColor: textColor,
              pointerEvents: "none",
              whiteSpace: "pre-wrap",
              overflowWrap: "normal",
              wordBreak: "keep-all",
              hyphens: "none",
            }}
            className="relative z-10"
          >
            {pageText ? (
              (() => {
                const modifiedPageText = pageText.replace(
                  /((?:\n){2,})/g,
                  (match) =>
                    Array(match.length - 1)
                      .fill("¥")
                      .join("") + "\n"
                );

                const tokens = modifiedPageText
                  .split(/([ \t]+|\n+|¥+)/)
                  .filter(Boolean);

                const rendered = [];
                let globalCharIndex = startIndex;

                for (let t = 0; t < tokens.length; t++) {
                  const token = tokens[t];

                  if (token.includes("\n") || token.includes("¥")) {
                    token.split("").forEach((ch) => {
                      if (ch === "\n") {
                        rendered.push(
                          <span
                            key={`nl-${globalCharIndex++}`}
                            style={{
                              display: "block",
                              width: "100%",
                              height: "0px",
                            }}
                          >
                            {" "}
                          </span>
                        );
                      } else if (ch === "¥") {
                        rendered.push(
                          <span
                            key={`br-${globalCharIndex++}`}
                            style={{
                              display: "block",
                              width: "100%",
                              height: `${LINE_SPACING}px`,
                            }}
                          >
                            {" "}
                          </span>
                        );
                      }
                    });
                    continue;
                  }

                  if (/^[ \t]+$/.test(token)) {
                    let spaceToken = token;
                    if (wordSpacing === "large") {
                      spaceToken = token.replace(/ /g, "  ");
                    }

                    rendered.push(
                      <span
                        key={`sp-${globalCharIndex++}`}
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {spaceToken}
                      </span>
                    );
                    continue;
                  }

                  const chars = [];
                  for (const einheit of zerlegeInEinheiten(token)) {
                    /* Formel (Bruch, Wurzel, x², π …): wird in der Schrift
                       der Seite gesetzt, siehe mathe/darstellen.js. */
                    if (einheit.mathe) {
                      chars.push(
                        <span
                          key={`mx-${globalCharIndex}`}
                          style={tippStil(globalCharIndex)}
                          dangerouslySetInnerHTML={{
                            __html: formelHTML(
                              einheit.quelle,
                              matheOptionen(globalCharIndex)
                            ),
                          }}
                        />
                      );
                      globalCharIndex += einheit.laenge;
                      continue;
                    }
                    const char = einheit.ch;
                    if (!isCharSupportedByFont(char, font)) {
                      globalCharIndex += einheit.laenge;
                      continue;
                    }

                    const variation = getCharVariation(globalCharIndex);

                    const charXJitterMax =
                      (messiness / 100) *
                      0.6 *
                      (gnPapier ? gnPapier.faktor : 1);
                    const charXJitter =
                      (seededRandomLocal(globalCharIndex * 13.37) - 0.5) *
                      2 *
                      charXJitterMax;

                    /* Zeichen aus der Ersatzschrift (Ziffern, + = ( ) bei
                       Schriften ohne eigene) auf die Grundlinie der
                       Hauptschrift holen. Gemessen in mathe/metrik.js — passen
                       beide Schriften zusammen, ist der Wert 0. */
                    const zeichenSchrift = matheMetrik(getFontFamily(font));
                    const ersatzAbsenkung =
                      zeichenSchrift.absenkung(char) *
                      fontSettings.fontsize *
                      (1 + variation.sizeVariation);
                    const yJitter =
                      (gnPapier
                        ? variation.yOffset * (messiness / 10) * gnPapier.faktor
                        : variation.yOffset * (messiness / 10)) +
                      ersatzAbsenkung;

                    chars.push(
                      <span
                        key={`ch-${globalCharIndex}`}
                        data-gn-ch={char}
                        style={{
                          display: "inline-block",
                          lineHeight: "0px", // <-- ZWINGEND NOTWENDIG
                          fontSize: `${
                            fontSettings.fontsize *
                            (1 + variation.sizeVariation)
                          }px`,
                          letterSpacing: `${berechneLaufweite(
                            variation,
                            fontSettings.fontsize
                          )}px`,
                          transform: `translateX(${charXJitter}px) translateY(${yJitter}px)`,
                          ...tippStil(globalCharIndex),
                        }}
                      >
                        {char}
                      </span>
                    );

                    globalCharIndex += einheit.laenge;
                  }

                  rendered.push(
                    <span
                      key={`wd-${globalCharIndex}-${t}`}
                      style={{ display: "inline-block", whiteSpace: "nowrap" }}
                    >
                      {chars}
                    </span>
                  );
                }

                return rendered;
              })()
            ) : (
              <span className="text-gray-400">Dein Text erscheint hier...</span>
            )}
          </div>

          {/* DER NEUE VECTOR-CANVAS (speichert Zustand & referenziert Undo/Redo) */}
          <div
            className="absolute inset-0 z-20"
            style={{ pointerEvents: drawingTool ? "auto" : "none" }}
          >
            <ReactSketchCanvas
              key={drawingKey} // stabil pro Seite, nicht abhängig vom Tool
              ref={sketchCanvasRef}
              onChange={handleCanvasChange}
              onStrokeEnd={persistPaths} // NEU: zusätzlich sichern
              style={{ width: "100%", height: "100%", border: "none" }}
              strokeWidth={currentStrokeWidth}
              strokeColor={currentStrokeColor}
              eraserWidth={brushSize * 8}
              exportWithBackgroundImage={false}
              canvasColor="transparent"
              mode={isEraser ? "eraser" : "pen"}
            />
          </div>
        </div>
      </div>
    );
  };

  const today = new Date();
  // Erstellt den Datumsschlüssel im Format "YYYY-MM-DD", z.B. "2025-12-15"
  const dateKey = today.toISOString().substring(0, 10);

  // Die Referenz auf das zentrale Download-Statistik-Dokument
  const counterRef = doc(db, "metrics", "download_stats");
  // Referenz auf das zentrale Handschrift-Statistik-Dokument
  const fontUsageRef = doc(db, "metrics", "font_usage");
  const inputsCollectionRef = collection(db, "inputs");

  async function downloadAsPDF() {
    if (!user) {
      showToast("Melde dich kurz an, dann geht es weiter.", "error");
      handleLogin();
      return;
    }

    if (pages.length === 0) return;

    // Erst abbuchen, dann erzeugen. Vorher lief die Prüfung nur über den
    // lokalen `totalCredits` im Klick-Handler und der Abzug erst NACH dem
    // Speichern — wer schneller klickte als Firestore schrieb, bekam mehrere
    // Dateien für ein Guthaben. Die Transaktion in credits.js entscheidet
    // jetzt anhand des Serverstands, und ohne Deckung entsteht keine Datei.
    const spend = await spendDownloadCredit();
    if (!spend.ok) {
      if (spend.reason === "empty") {
        promptForMoreDownloads();
      } else if (
        spend.reason === "signed-out" ||
        spend.reason === "no-account"
      ) {
        showToast("Melde dich kurz an, dann geht es weiter.", "error");
        handleLogin();
      } else {
        showToast(
          "Dein Download-Guthaben ließ sich gerade nicht prüfen. Versuch es nochmal.",
          "error"
        );
      }
      return;
    }

    setIsGenerating(true);
    setIsDownloadMode(true);
    // Kurze Verzögerung, um den Download-Modus-Style zu laden
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // 🚨 NEU: DYNAMISCHER IMPORT HIER!
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      await document.fonts.ready;
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true, // PDF-Kompression aktivieren
      });

      for (let i = 0; i < pages.length; i++) {
        const pageElement = pageRefs.current[i]?.current;
        if (!pageElement) continue;

        const canvas = await html2canvas(pageElement, {
          backgroundColor: paperColor,
          scale: 5,
          useCORS: true,
          logging: false,
          width: A4_WIDTH,
          height: A4_HEIGHT,
        });

        if (i > 0) pdf.addPage();

        // JPEG statt PNG mit 85% Qualität = massiv kleinere Datei
        pdf.addImage(
          canvas.toDataURL("image/jpeg", 0.85),
          "JPEG",
          0,
          0,
          210,
          297,
          undefined,
          "FAST" // Kompressionsalgorithmus
        );
      }

      // Beispiel für downloadAsPDF (Am Ende der Funktion einfügen):
      pdf.save("schriftbot.pdf");
      // ----------------------------------------------------
      // METRIK 1: HANDSCHRIFT-NUTZUNG ZÄHLEN (Nur Gesamt)
      // ----------------------------------------------------
      const fontKey = font.toLowerCase();

      updateDoc(fontUsageRef, {
        // Gesamt-Zähler
        [`total_${fontKey}_uses`]: increment(1),
      })
        .then(() => {
          console.log(
            `✅ PDF: Font-Nutzung (${font}) erfolgreich gezählt. (Write 1/2)`
          );
        })
        .catch((error) => {
          console.error(
            "❌ Fehler beim Zählen der Font-Nutzung (PDF): ",
            error
          );
        });

      // ----------------------------------------------------
      // METRIK 2: DOWNLOADS ZÄHLEN (Gesamt & Täglich)
      // ----------------------------------------------------
      updateDoc(counterRef, {
        // Gesamt-Zähler
        pdf_downloads: increment(1),
        // Täglicher Zähler
        [`daily_pdf_downloads.${dateKey}`]: increment(1),
      })
        .then(() => {
          console.log(
            "✅ PDF-Download gesamt und täglich erfolgreich gezählt. (Write 2/2)"
          );
        })
        .catch((error) => {
          console.error(
            "❌ Fehler beim Inkrementieren des PDF-Zählers: ",
            error
          );
        });

      // await addDoc(inputsCollectionRef, {
      //   text: text,
      //   font: font,
      //   pages: pages.length,
      //   createdAt: serverTimestamp(),
      //   source: "pdf_download",
      // });

      // ----------------------------------------------------
    } catch (error) {
      console.error(error);
      // Nichts bekommen, nichts bezahlt: den abgebuchten Download zurück.
      if (spend.spent) await refundDownloadCredit(spend.debitId);
      showToast(
        "PDF konnte nicht erstellt werden. Versuch es nochmal.",
        "error"
      );
    }

    setIsGenerating(false);
    setIsDownloadMode(false);
  }

  /* GoodNotes-Datei: jeder Buchstabe der eigenen Handschrift als Stift-Strich,
     deshalb in GoodNotes einzeln radierbar. Positionen kommen aus der
     Vorschau, damit alles genau so sitzt wie auf dem Bildschirm. Abgebucht
     wird wie bei PDF und Bild, bei einem Fehlschlag zurückgebucht. */
  async function downloadAsGoodnotes() {
    if (!user) {
      showToast("Melde dich kurz an, dann geht es weiter.", "error");
      handleLogin();
      return;
    }
    if (pages.length === 0 || !eigeneHandschriftAktiv) return;

    const spend = await spendDownloadCredit();
    if (!spend.ok) {
      if (spend.reason === "empty") {
        promptForMoreDownloads();
      } else if (
        spend.reason === "signed-out" ||
        spend.reason === "no-account"
      ) {
        showToast("Melde dich kurz an, dann geht es weiter.", "error");
        handleLogin();
      } else {
        showToast(
          "Dein Download-Guthaben ließ sich gerade nicht prüfen. Versuch es nochmal.",
          "error"
        );
      }
      return;
    }

    setIsGenerating(true);
    try {
      const daten = await ladeStriche(user.uid);
      if (!daten || !daten.glyphen || !Object.keys(daten.glyphen).length) {
        throw new Error("keine-striche");
      }
      await document.fonts.ready;
      const { baueGoodnotesDateien, ladeHerunter } = await import(
        "./handschrift/goodnotesExport"
      );
      const { dateien, fehlend } = await baueGoodnotesDateien(
        daten,
        pages.map((_, i) => pageRefs.current[i]?.current),
        {
          papier: gnPapier
            ? lineStyle
            : { lines: "liniert", grid: "kariert", blank: "blanko" }[
                lineStyle
              ] || "liniert",
          farbe: textColor,
          papierFarbe: paperColor,
          umriss: umrissBreite,
        }
      );
      for (const d of dateien) {
        ladeHerunter(d.blob, d.name);
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      const fehlt = fehlend.filter((c) => c.trim());
      // "Seite hinzufügen > Importieren" in einem Heft nimmt in GoodNotes nur
      // PDF und Bilder, dort ist die Datei grau (goodnotesExport.test.js).
      const importHinweis =
        "In GoodNotes in der Bibliothek über „Neu → Importieren“ öffnen, nicht über „Seite hinzufügen“.";
      showToast(
        fehlt.length
          ? `GoodNotes-Datei geladen. Nicht gezeichnet und deshalb weggelassen: ${fehlt
              .slice(0, 12)
              .join(" ")}. ${importHinweis}`
          : `GoodNotes-Datei geladen. ${importHinweis}`,
        fehlt.length ? "error" : "success",
        9000
      );
    } catch (error) {
      console.error(error);
      // Nichts bekommen, nichts bezahlt: den abgebuchten Download zurück.
      if (spend.spent) await refundDownloadCredit(spend.debitId);
      showToast(
        error && error.message === "keine-striche"
          ? "Für diese Handschrift gibt es keine Striche. Zeichne sie im Editor neu, dann geht der GoodNotes-Export."
          : "Die GoodNotes-Datei konnte nicht erstellt werden. Versuch es nochmal.",
        "error"
      );
    }
    setIsGenerating(false);
  }

  async function downloadAsImage() {
    if (!user) {
      showToast("Melde dich kurz an, dann geht es weiter.", "error");
      handleLogin();
      return;
    }

    if (pages.length === 0) return;

    // Erst abbuchen, dann erzeugen. Vorher lief die Prüfung nur über den
    // lokalen `totalCredits` im Klick-Handler und der Abzug erst NACH dem
    // Speichern — wer schneller klickte als Firestore schrieb, bekam mehrere
    // Dateien für ein Guthaben. Die Transaktion in credits.js entscheidet
    // jetzt anhand des Serverstands, und ohne Deckung entsteht keine Datei.
    const spend = await spendDownloadCredit();
    if (!spend.ok) {
      if (spend.reason === "empty") {
        promptForMoreDownloads();
      } else if (
        spend.reason === "signed-out" ||
        spend.reason === "no-account"
      ) {
        showToast("Melde dich kurz an, dann geht es weiter.", "error");
        handleLogin();
      } else {
        showToast(
          "Dein Download-Guthaben ließ sich gerade nicht prüfen. Versuch es nochmal.",
          "error"
        );
      }
      return;
    }

    setIsDownloadMode(true);
    setIsGenerating(true);
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // 🚨 NEU: DYNAMISCHER IMPORT HIER!
      const html2canvas = (await import("html2canvas")).default;

      await document.fonts.ready;
      const imageUrls = [];

      for (const ref of pageRefs.current) {
        if (ref.current) {
          // ... (bestehender Code zur Canvas-Erstellung) ...
          const element = document.createElement("div");
          element.style.cssText = `position: absolute; top: 0; left: 0; z-index: -100; width: ${A4_WIDTH}px;`;

          const cloned = ref.current.cloneNode(true);
          cloned.style.marginBottom = "0px";
          // WICHTIG: Vorschau-Skalierung für den sauberen Export entfernen!
          cloned.style.transform = "none";
          cloned.style.transformOrigin = "unset";

          element.appendChild(cloned);
          document.body.appendChild(element);

          const canvas = await html2canvas(element, {
            backgroundColor: paperColor,
            scale: 5,
            useCORS: true,
            logging: false,
            width: A4_WIDTH,
            height: A4_HEIGHT, // <-- Wichtig, damit das Bild die vollen Dimensionen ausnutzt
          });

          const dataUrl = canvas.toDataURL("image/png");
          imageUrls.push(dataUrl);

          document.body.removeChild(element);
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      setGeneratedImages(imageUrls);
      setShowImageGallery(true);
      // ----------------------------------------------------
      // METRIK 1: HANDSCHRIFT-NUTZUNG ZÄHLEN (Nur Gesamt)
      // ----------------------------------------------------
      const fontKey = font.toLowerCase();

      updateDoc(fontUsageRef, {
        // Gesamt-Zähler
        [`total_${fontKey}_uses`]: increment(1),
      })
        .then(() => {
          console.log(
            `✅ Image: Font-Nutzung (${font}) erfolgreich gezählt. (Write 1/2)`
          );
        })
        .catch((error) => {
          console.error(
            "❌ Fehler beim Zählen der Font-Nutzung (Image): ",
            error
          );
        });

      // ----------------------------------------------------
      // METRIK 2: DOWNLOADS ZÄHLEN (Gesamt & Täglich)
      // ----------------------------------------------------
      updateDoc(counterRef, {
        // Gesamt-Zähler
        image_downloads: increment(1),
        // Täglicher Zähler
        [`daily_image_downloads.${dateKey}`]: increment(1),
      })
        .then(() => {
          console.log(
            "✅ Bild-Download gesamt und täglich erfolgreich gezählt. (Write 2/2)"
          );
        })
        .catch((error) => {
          console.error(
            "❌ Fehler beim Inkrementieren des Bild-Zählers: ",
            error
          );
        });

      // await addDoc(inputsCollectionRef, {
      //   text: text,
      //   font: font,
      //   pages: pages.length,
      //   createdAt: serverTimestamp(),
      //   source: "image_download",
      // });
      // ----------------------------------------------------
    } catch (e) {
      console.error(e);
      // Nichts bekommen, nichts bezahlt: den abgebuchten Download zurück.
      if (spend.spent) await refundDownloadCredit(spend.debitId);
      showToast(
        "Bild konnte nicht gespeichert werden. Versuch es nochmal.",
        "error"
      );
    }

    setIsDownloadMode(false);
    setIsGenerating(false);
  }

  useEffect(() => {
    const updateScale = () => {
      if (wrapperRef.current && !isDownloadMode) {
        const wrapperWidth = wrapperRef.current.offsetWidth;
        const scale = wrapperWidth / A4_WIDTH;
        setPreviewScale(scale);
      } else {
        setPreviewScale(1);
      }
    };
    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [isDownloadMode]);

  // const handleFontChange = (e) => {
  //   const selectedFont = e.target.value;

  //   if (selectedFont === "meine_handschrift") {
  //     if (!user) {
  //       // Nicht eingeloggt? Login triggern
  //       if (
  //         window.confirm(
  //           "Um deine eigene Handschrift zu nutzen, musst du dich kurz anmelden. Jetzt einloggen?"
  //         )
  //       ) {
  //         handleLogin();
  //       }
  //       e.target.value = font; // Zurücksetzen
  //       return;
  //     }

  //     if (!customChars) {
  //       // Eingeloggt, aber noch nichts gezeichnet? Editor öffnen
  //       setIsEditorOpen(true);
  //       e.target.value = font;
  //       return;
  //     }

  //     setFont("meine_handschrift");
  //   } else {
  //     setFont(selectedFont);
  //   }
  // };

  const handleFontChange = (e) => {
    const selectedFont = e.target.value;
    // Normale Schriftart ändern
    setFont(selectedFont);
  };

  const [showUpgradeOverlay, setShowUpgradeOverlay] = useState(false);

  useEffect(() => {
    if (isGenerating && !isUnlimited) {
      setShowUpgradeOverlay(true);
    }
  }, [isGenerating, isUnlimited]);

  // Feedback
  // Oben bei deinen Imports sicherstellen, dass addDoc dabei ist:
  // import { collection, addDoc, serverTimestamp } from "firebase/firestore";
  // import { db } from "./deine-firebase-config";

  // Innerhalb der Komponente:
  const [showFeedback, setShowFeedback] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Während eines Downloads: Bewertung einfordern, solange dieses Konto noch
  // keine abgegeben hat. Der Download selbst läuft unabhängig weiter; das
  // Popup bleibt, bis gesendet oder das Kreuz geklickt wird.
  useEffect(() => {
    if (!isGenerating || !user) return;
    try {
      if (
        localStorage.getItem(`feedbackDone_${user.uid}`) ||
        localStorage.getItem("feedbackDone")
      ) {
        return;
      }
    } catch (e) {
      /* localStorage nicht verfügbar: Popup trotzdem zeigen */
    }
    setRating(0);
    setFeedbackText("");
    setSubmitted(false);
    setShowFeedback(true);
  }, [isGenerating, user]);

  const sendFeedbackToFirestore = async () => {
    if (!user) return;
    if (rating === 0) {
      showToast("Bitte wähle mindestens einen Stern aus.", "error");
      return;
    }

    try {
      // Dokument-ID = Nutzer-ID: Pro Konto gibt es höchstens eine Bewertung.
      // Die Firestore-Regeln erlauben nur `create`, ein zweiter Versuch wird
      // dort abgelehnt.
      await setDoc(doc(db, "feedback", user.uid), {
        rating: rating,
        text: feedbackText,
        timestamp: serverTimestamp(),
        userEmail: user?.email || "Anonym",
        userId: user.uid,
      });

      setSubmitted(true);
      localStorage.setItem(`feedbackDone_${user.uid}`, "true"); // Verhindert erneutes Pop-up

      // Nach 3 Sekunden automatisch schließen
      setTimeout(() => setShowFeedback(false), 2000);
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      if (error?.code === "permission-denied") {
        // Dieses Konto hat schon bewertet.
        localStorage.setItem(`feedbackDone_${user.uid}`, "true");
        setShowFeedback(false);
        return;
      }
      showToast("Da ging was schief. Versuch es später nochmal.", "error");
    }
  };

  const downloadBenutzungshandbuch = () => {
    const link = document.createElement("a");
    link.href = "/Benutzungshandbuch.pdf";
    link.download = "Benutzungshandbuch.pdf";
    link.click();
  };

  // Hat der Nutzer überhaupt eine eigene Handschrift? Das trifft auf zwei
  // Gruppen zu: wer selbst im Browser gezeichnet hat (liegt in Firestore),
  // und die Bestandsnutzer, deren Schriftdatei noch im Bundle mitgeliefert
  // wird. getFontFamily kennt beide Wege, deshalb reicht der Vergleich gegen
  // die Standardschrift.
  const hasCustomHandwriting = () => {
    if (!user) return false;
    return getFontFamily(user.uid) !== getFontFamily("default");
  };

  /* Ist gerade die eigene Handschrift eingestellt? Gilt für beide Wege — die
     im Browser gezeichnete und die mitgelieferte TTF von Bestandsnutzern. */
  const eigeneHandschriftAktiv = !!user && font === user.uid;

  /* Der eine Knopf für die eigene Handschrift. Je nachdem, woran es fehlt,
     führt er woanders hin — aber er ist immer sichtbar, damit niemand raten
     muss, ob es die Funktion überhaupt gibt. */
  const handleEigeneHandschriftNutzen = () => {
    if (!user) {
      handleLogin();
      return;
    }
    if (!ownHandwritingAllowed) {
      // Ohne Abo das Testangebot, mit kleinerem Abo die Preisübersicht.
      zeigeAngebotOderPreise();
      return;
    }
    if (!hasCustomHandwriting()) {
      setZeigeSchriftEditor(true); // Abo da, aber noch nichts gezeichnet
      return;
    }
    setFont(eigeneHandschriftAktiv ? "Sebastian" : user.uid);
  };

  // Füge dies zu deinen anderen useStates hinzu:
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showOwnHandwritingModal, setShowOwnHandwritingModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  // Kleines Hinweis-Popup mit einem blauen Knopf: { titel?, text, knopf, aktion? }
  const [hinweis, setHinweis] = useState(null);

  // ── Testangebot „eigene Handschrift 7 Tage gratis" ──
  // Für alle ohne Abo. Zwei Arten von Anlass:
  //  - beiläufig: nach einem Download (also auch erst nach der Bewertung)
  //    oder nach 30 Sekunden auf der Seite, wenn angemeldet. Nie über ein
  //    anderes Fenster, pro Gerät höchstens einmal am Tag.
  //  - ausdrücklich: Guthaben leer oder Knopf „eigene Handschrift". Dann
  //    sofort und statt der Bezahlschranke (zeigeTestangebotJetzt).
  const TESTANGEBOT_KEY = "sb-testangebot-gezeigt";
  const TESTANGEBOT_PAUSE_MS = 24 * 60 * 60 * 1000;
  const [testangebotFaellig, setTestangebotFaellig] = useState(false);
  const [zeigeTestangebot, setZeigeTestangebot] = useState(false);
  const testangebotGezeigtRef = useRef(false);

  // Anonyme Zähler für die Admin-Statistik (/admin): wie oft das Angebot
  // erschien und wie oft jemand auf „testen" geklickt hat.
  const zaehleTestangebot = (feld) => {
    const dateKey = new Date().toISOString().substring(0, 10);
    updateDoc(doc(db, "metrics", "site_stats"), {
      [feld]: increment(1),
      [`daily_${feld}.${dateKey}`]: increment(1),
    }).catch(() => {});
  };
  const [cookieBannerOffen, setCookieBannerOffen] =
    useState(cookieBannerPending);

  useEffect(() => {
    const cookieEntschieden = () => setCookieBannerOffen(false);
    window.addEventListener(COOKIE_DECIDED_EVENT, cookieEntschieden);
    return () =>
      window.removeEventListener(COOKIE_DECIDED_EVENT, cookieEntschieden);
  }, []);

  // Die 30 Sekunden zählen nur für Angemeldete.
  const angemeldet = Boolean(user);
  useEffect(() => {
    if (!angemeldet) return;
    const timer = setTimeout(() => setTestangebotFaellig(true), 30000);
    return () => clearTimeout(timer);
  }, [angemeldet]);

  const ohneAbo =
    !isUnlimited && (currentPlan === "" || currentPlan === "expired");
  const anderesFensterOffen = Boolean(
    cookieBannerOffen ||
      isGenerating ||
      showFeedback ||
      pendingPlan ||
      showPricingModal ||
      showUnlockModal ||
      showOwnHandwritingModal ||
      hinweis ||
      showAIModal ||
      zeigeSchriftEditor ||
      showDeleteAccount
  );

  useEffect(() => {
    if (!testangebotFaellig || testangebotGezeigtRef.current) return;
    if (!abostatusBekannt || !ohneAbo || testphaseGenutzt) return;
    if (anderesFensterOffen) return;
    try {
      const zuletzt = Number(localStorage.getItem(TESTANGEBOT_KEY)) || 0;
      if (Date.now() - zuletzt < TESTANGEBOT_PAUSE_MS) return;
      localStorage.setItem(TESTANGEBOT_KEY, String(Date.now()));
    } catch {
      // Ohne Speicher (privates Fenster) zählt nur dieser Besuch.
    }
    testangebotGezeigtRef.current = true;
    setZeigeTestangebot(true);
    zaehleTestangebot("trial_offer_shown");
  }, [
    testangebotFaellig,
    abostatusBekannt,
    ohneAbo,
    testphaseGenutzt,
    anderesFensterOffen,
  ]);

  // Ausdrücklicher Anlass: Angebot sofort zeigen, ohne Tagesgrenze — hier
  // will jemand gerade genau das. `danach` läuft, wenn es weggeklickt wird.
  // false heißt: dieses Konto bekommt kein Angebot (Abo, schon getestet,
  // oder gerade läuft die Bewertung), der Aufrufer zeigt dann das Übliche.
  const testangebotDanachRef = useRef(null);
  const testangebotMoeglich = abostatusBekannt && ohneAbo && !testphaseGenutzt;
  const zeigeTestangebotJetzt = (danach = null) => {
    if (!testangebotMoeglich || showFeedback) return false;
    try {
      localStorage.setItem(TESTANGEBOT_KEY, String(Date.now()));
    } catch {
      // ohne Speicher: egal, die Tagesgrenze gilt dann nur für diesen Besuch
    }
    testangebotGezeigtRef.current = true;
    testangebotDanachRef.current = danach;
    setZeigeTestangebot(true);
    zaehleTestangebot("trial_offer_shown");
    return true;
  };

  // Überall, wo sonst die Preisübersicht aufging: erst das Testangebot,
  // weggeklickt dann die Preisübersicht.
  const zeigeAngebotOderPreise = () => {
    const preise = () => setShowPricingModal(true);
    if (!zeigeTestangebotJetzt(preise)) preise();
  };

  const schliesseTestangebot = () => {
    setZeigeTestangebot(false);
    const danach = testangebotDanachRef.current;
    testangebotDanachRef.current = null;
    if (danach) danach();
  };

  // Guthaben leer: erst das Testangebot, und wer das wegklickt, landet wie
  // bisher dort, wo es neues Guthaben gibt — vor dem ersten Kauf das
  // Freischalt-Fenster (dort lassen sich auch Münzen einlösen), danach die
  // Preisübersicht.
  const promptForMoreDownloads = () => {
    const bezahlschranke = () =>
      hasPREMIUM ? setShowPricingModal(true) : setShowUnlockModal(true);
    if (!zeigeTestangebotJetzt(bezahlschranke)) bezahlschranke();
  };

  // Zentrale Logik für die Download-Buttons (Bild & PDF).
  // Premium ist keine Pflicht mehr: Jedes neue Konto bekommt vom Server
  // Gratis-Downloads, dazu kommen Münzen aus den Spielen (10 Münzen = 1
  // gratis PDF).
  //
  // Die Prüfung hier ist nur die schnelle Antwort für die Oberfläche — sie
  // arbeitet mit dem lokalen Stand. Verbindlich entscheidet die Abbuchung in
  // downloadAsPDF/downloadAsImage gegen den Serverstand.
  const handleDownloadClick = (type) => {
    // Erscheint erst, wenn Download und Bewertung durch sind (siehe oben).
    setTestangebotFaellig(true);
    if (!user) {
      handleLogin();
      return;
    }
    if (type === "goodnotes") {
      /* Der GoodNotes-Export setzt jeden Buchstaben aus den gezeichneten
         Strichen zusammen. Ohne das Abo mit eigener Handschrift: erst ein
         Hinweis, dessen Knopf in die Paywall führt. Mit Abo, aber noch nichts
         gezeichnet: Hinweis, der in den Editor führt. */
      if (!ownHandwritingAllowed) {
        // Ohne Abo gleich das Testangebot — es nennt den GoodNotes-Export.
        if (zeigeTestangebotJetzt(() => setShowPricingModal(true))) return;
        setHinweis({
          titel: "GoodNotes-Export freischalten",
          text: "Der Export als GoodNotes-Datei ist im Tarif mit eigener Handschrift enthalten. Jeder Buchstabe wird ein Strich, den du in GoodNotes radieren kannst.",
          knopf: "Freischalten",
          aktion: () => setShowPricingModal(true),
        });
        return;
      }
      if (!eigeneSchriftUid || eigeneSchriftUid !== user.uid) {
        setHinweis({
          text: "Zeichne zuerst deine Handschrift, dann geht der GoodNotes-Export.",
          knopf: "Handschrift zeichnen",
          aktion: () => setZeigeSchriftEditor(true),
        });
        return;
      }
      if (!eigeneHandschriftAktiv) {
        setFont(user.uid);
        setHinweis({
          text: "Deine Handschrift ist jetzt aktiv. Klicke noch einmal auf GoodNotes.",
          knopf: "OK",
        });
        return;
      }
    }
    if (!isUnlimited && totalCredits <= 0) {
      promptForMoreDownloads();
      return;
    }
    if (type === "image") {
      downloadAsImage();
    } else if (type === "goodnotes") {
      downloadAsGoodnotes();
    } else {
      downloadAsPDF();
    }
  };

  const getToolButtonClass = (tool) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
      drawingTool === tool
        ? tool === null
          ? "bg-zinc-200 text-zinc-900 border-zinc-300 dark:bg-zinc-800 dark:text-white dark:border-zinc-700"
          : "bg-black text-white border-black dark:bg-white dark:text-black"
        : "bg-white/80 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-200"
    }`;

  return (
    <div
      className="home-page flex flex-col min-h-screen lg:flex-row relative"
      style={{
        background: ds.color.canvas,
        color: ds.color.ink,
        fontFamily: ds.font.body,
      }}
    >
      {/* GLOBAL STYLES
          Der frühere Block hier hat `body { font-family: 'Inter' }` gesetzt
          und damit die Typografie des Designsystems auf der wichtigsten
          Seite wieder ausgehebelt. Er setzt jetzt nur noch das, was sich
          nicht über Tokens ausdrücken lässt: Scrollbar, Select-Pfeil,
          Farbwähler, und die Pixel-Optik der Spiele-Promo (die bewusst
          außerhalb des Systems steht). */}
      <style>{`
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: ${ds.color.soft}; border-radius: 999px; }
      ::-webkit-scrollbar-thumb:hover { background: ${ds.color.raised}; }

      .home-page select {
        appearance: none;
        background-repeat: no-repeat;
        background-position: right 18px center;
        background-size: 10px;
      }

      input[type="color"] {
        -webkit-appearance: none;
        border: none;
        width: 36px;
        height: 36px;
        border-radius: 999px;
        overflow: hidden;
        cursor: pointer;
        background: transparent;
        padding: 0;
      }
      input[type="color"]::-webkit-color-swatch-wrapper { padding: 0; }
      input[type="color"]::-webkit-color-swatch { border: none; border-radius: 999px; }

      /* Regler — Bahn in der Füllfarbe, Griff in der Akzentfarbe. */
      .sb-range {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        height: 6px;
        border-radius: 999px;
        background: ${ds.color.soft};
        cursor: pointer;
        outline: none;
      }
      .sb-range::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 20px;
        height: 20px;
        border-radius: 999px;
        background: ${ds.color.accent};
        border: none;
        cursor: pointer;
      }
      .sb-range::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border-radius: 999px;
        background: ${ds.color.accent};
        border: none;
        cursor: pointer;
      }

      @keyframes fadeIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      .animate-fade-in { animation: fadeIn .8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

      /* Tipp-Animation der Buchstaben in der Vorschau (siehe tippen) */
      @keyframes sbTipp { from { opacity: 0; } to { opacity: 1; } }

      /* Spiele-Promo: bewusst außerhalb des Systems (Retro-Optik). */
      .pixel-btn {
        background: #f4b41a;
        color: #1a1200;
        border: 3px solid #1a1200;
        border-radius: 0;
        box-shadow: 3px 3px 0 #1a1200;
        transition: transform 0.05s, box-shadow 0.05s;
      }
      .pixel-btn:hover:not(:disabled) { transform: translate(1px, 1px); box-shadow: 2px 2px 0 #1a1200; }
      .pixel-btn:active:not(:disabled) { transform: translate(3px, 3px); box-shadow: 0 0 0 #1a1200; }
      .pixel-btn:disabled { opacity: 0.45; cursor: not-allowed; }
      .pixel-confetti {
        position: absolute;
        top: -14px;
        animation-name: pixelConfettiFall;
        animation-timing-function: steps(14, end);
        animation-iteration-count: infinite;
      }
      @keyframes pixelConfettiFall {
        from { transform: translateY(0); }
        to { transform: translateY(440px); }
      }
    `}</style>

      {/* --- MODALS --- */}

      {/* ── PLAN-AUSWAHL ──────────────────────────────────────────────
          Vorher waren das zwei getrennte Modals mit zwei verschiedenen
          Preislisten (7,99 € vs. 9,99 € für denselben Tarif). Beide
          rendern jetzt aus derselben Leiter in src/plans.js, damit der
          Preis vor dem Bestellbutton immer DER Preis ist.
          `showUnlockModal` öffnet sie aus dem Download-Klick heraus,
          `showPricingModal` aus dem Upgrade-Klick. Der Unterschied ist
          nur noch die Überschrift.                                     */}
      {(showUnlockModal || showPricingModal) && (
        <div
          className="fixed inset-0 z-[200] w-full h-[100dvh] overflow-y-auto"
          style={{
            background: ds.scrim,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
          }}
        >
          <button
            onClick={() => {
              setShowUnlockModal(false);
              setShowPricingModal(false);
            }}
            aria-label="Schließen"
            className="fixed top-5 right-5 z-50 inline-flex items-center justify-center"
            style={{
              width: 44,
              height: 44,
              borderRadius: ds.radius.pill,
              border: "none",
              background: ds.color.soft,
              color: ds.color.muted,
              cursor: "pointer",
            }}
          >
            <X size={20} />
          </button>

          <div className="min-h-full w-full flex flex-col items-center justify-center py-10 md:py-16 px-5">
            {/* Header — auf iPhone knapper, damit die zweite Karte
                (Unlimited) ohne Scrollen mit ins Blickfeld kommt. */}
            <div className="text-center mb-6 md:mb-10 w-full max-w-xl">
              <h2
                style={{
                  fontFamily: ds.font.display,
                  fontWeight: 400,
                  fontSize: ds.text.h1,
                  lineHeight: ds.leading.tight,
                  letterSpacing: ds.tracking.tight,
                  color: ds.color.ink,
                  margin: 0,
                }}
              >
                {showUnlockModal
                  ? "Ohne Wasserzeichen speichern"
                  : "Mehr freischalten"}
              </h2>
              <p
                style={{
                  fontFamily: ds.font.body,
                  fontSize: 18,
                  lineHeight: 1.65,
                  color: ds.color.muted,
                  margin: "10px auto 0",
                  maxWidth: 460,
                }}
              >
                {showUnlockModal
                  ? "Wähle einen Tarif, um deinen Text als Bild oder PDF zu speichern."
                  : "Wähle den Tarif, der zu dir passt. Monatlich, jederzeit kündbar."}
              </p>

              {showUnlockModal && (
                <Link
                  to="/games"
                  onClick={() => setShowUnlockModal(false)}
                  className="sb-link inline-flex items-center gap-2 mt-3"
                  style={{
                    fontFamily: ds.font.body,
                    fontSize: 14,
                    fontWeight: 600,
                    color: ds.color.ink,
                  }}
                >
                  <CoinIcon size={14} /> Oder sammle Münzen: {COINS_PER_PDF}{" "}
                  Münzen = 1 gratis Download
                </Link>
              )}
            </div>

            {/* Tarife — bewusst nur 2, kein drittes Kästchen daneben: eine
                Mitte zwischen "billig" und "alles" wurde in echten Tests
                (3,49 €, 6,49 €, beide in einer Dreier-Reihe) zweimal
                ignoriert, unabhängig vom Preis. Kleiner Preisabstand,
                großer Wertabstand zwischen den beiden verbleibenden
                Karten ist Absicht. */}
            <div
              className="w-full grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-3"
              style={{ maxWidth: 720 }}
            >
              {PLANS.map((plan) => {
                const featured = plan.featured;
                return (
                  <div
                    key={plan.id}
                    className="flex flex-col p-6 md:p-8 relative"
                    style={{
                      background: featured
                        ? "rgba(255,255,255,0.14)"
                        : "rgba(255,255,255,0.07)",
                      backdropFilter: "blur(32px) saturate(180%)",
                      WebkitBackdropFilter: "blur(32px) saturate(180%)",
                      borderRadius: ds.radius.lg,
                      border: featured
                        ? `2px solid ${ds.color.accent}`
                        : "1px solid rgba(255,255,255,0.14)",
                    }}
                  >
                    {featured && (
                      <span
                        style={{
                          position: "absolute",
                          top: -14,
                          left: 32,
                          background: ds.color.accent,
                          color: ds.color.accentInk,
                          fontFamily: ds.font.body,
                          fontSize: 13,
                          fontWeight: 600,
                          padding: "5px 14px",
                          borderRadius: ds.radius.pill,
                        }}
                      >
                        Beliebteste Wahl
                      </span>
                    )}

                    <h3
                      style={{
                        fontFamily: ds.font.display,
                        fontWeight: 400,
                        fontSize: ds.text.h3,
                        lineHeight: ds.leading.tight,
                        color: ds.color.ink,
                        margin: 0,
                      }}
                    >
                      {plan.name}
                    </h3>

                    <div style={{ margin: "10px 0 4px" }}>
                      <span
                        style={{
                          fontFamily: ds.font.body,
                          fontSize: 34,
                          fontWeight: 600,
                          letterSpacing: ds.tracking.tight,
                          color: ds.color.ink,
                        }}
                      >
                        {plan.price}
                      </span>
                      <span
                        style={{
                          fontFamily: ds.font.body,
                          fontSize: 16,
                          color: ds.color.muted,
                        }}
                      >
                        {" "}
                        / {CONTRACT_TERMS.interval}
                      </span>
                    </div>
                    {/* Testphase als Zusatz zum Preis, nicht als
                        durchgestrichener Preis: es ist kein Rabatt, und ein
                        Streichpreis wäre irreführend (§ 11 PAngV). */}
                    {plan.id === TRIAL_PLAN.id && testangebotMoeglich && (
                      <p
                        style={{
                          fontFamily: ds.font.body,
                          fontSize: 15,
                          fontWeight: 600,
                          color: ds.color.ink,
                          margin: "0 0 4px",
                        }}
                      >
                        Die ersten {TRIAL_PLAN.trialDays} Tage gratis
                      </p>
                    )}

                    <p
                      style={{
                        fontFamily: ds.font.body,
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: ds.color.muted,
                        margin: "0 0 16px",
                      }}
                    >
                      {plan.summary}
                    </p>

                    <ul
                      className="flex flex-col gap-3"
                      style={{
                        listStyle: "none",
                        margin: 0,
                        padding: 0,
                        marginBottom: 28,
                      }}
                    >
                      {plan.features.map((f) => {
                        const hervorgehoben = !!f.highlight;
                        return (
                          <li
                            key={f.label}
                            className="flex items-center gap-3"
                            style={{
                              fontFamily: ds.font.body,
                              fontSize: hervorgehoben ? 18 : 16,
                              fontWeight: hervorgehoben ? 700 : 400,
                              lineHeight: 1.5,
                              color: f.on ? ds.color.ink : ds.color.subtle,
                            }}
                          >
                            {f.on ? (
                              <Check size={18} className="flex-none" />
                            ) : (
                              <X size={18} className="flex-none" />
                            )}
                            {f.label}
                          </li>
                        );
                      })}
                    </ul>

                    {/* Dieser Knopf bestellt nichts — er öffnet die
                        Bestellübersicht. Die Pflichtbeschriftung nach
                        § 312j Abs. 3 BGB gehört an die Schaltfläche, die die
                        Bestellung auslöst, nicht hierher. Hier steht deshalb
                        der Auswahl-Schritt. */}
                    {plan.id === TRIAL_PLAN.id && testangebotMoeglich ? (
                      <button
                        onClick={() =>
                          handleBuyProcess(TRIAL_PLAN.priceId, { trial: true })
                        }
                        className="sb-btn mt-auto sb-btn-primary"
                        style={{ width: "100%" }}
                      >
                        {TRIAL_PLAN.trialDays} Tage gratis testen
                      </button>
                    ) : (
                      <button
                        onClick={() => handleBuyProcess(plan.priceId)}
                        className={`sb-btn mt-auto ${
                          plan.featured ? "sb-btn-primary" : "sb-btn-secondary"
                        }`}
                        style={{ width: "100%" }}
                      >
                        {plan.name} wählen
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* § 312j Abs. 2 BGB — Laufzeit, Kündigung, Steuerhinweis. */}
            <div
              className="w-full mt-6 p-6"
              style={{
                maxWidth: ds.container,
                background: ds.color.surface,
                borderRadius: ds.radius.lg,
              }}
            >
              <p
                style={{
                  fontFamily: ds.font.body,
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: ds.color.muted,
                  margin: 0,
                }}
              >
                {CONTRACT_TERMS.duration} {CONTRACT_TERMS.termination}{" "}
                {CONTRACT_TERMS.vat} Vor dem Kauf zeigen wir dir noch einmal
                alles zusammengefasst.{" "}
                <Link to="/agb" className="sb-link">
                  AGB &amp; Widerrufsbelehrung
                </Link>{" "}
                ·{" "}
                <Link to="/kuendigen" className="sb-link">
                  Kündigen
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      {showAIModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-md"
            onClick={() => {
              setShowAIModal(false);
              resetAIChat();
            }}
          ></div>
          <div className="relative w-full max-w-lg bg-white dark:bg-[#111] rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-stone-200 dark:border-stone-800">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100 dark:border-stone-800">
              <span className="text-[11px] uppercase font-bold tracking-widest text-stone-900 dark:text-white">
                Schriftbot AI
              </span>
              <button
                onClick={() => {
                  setShowAIModal(false);
                  resetAIChat();
                }}
                className="p-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex flex-col gap-4">
              {!aiResponse ? (
                <>
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] uppercase font-bold tracking-widest text-stone-400">
                      {aiContextText ? "Neue Anweisung" : "Deine Frage"}
                    </label>
                    {aiContextText && (
                      <div className="bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl px-4 py-3 text-xs text-stone-400 dark:text-stone-500 leading-relaxed max-h-[80px] overflow-y-auto whitespace-pre-wrap">
                        {aiContextText}
                      </div>
                    )}
                    <textarea
                      value={aiQuery}
                      onChange={(e) => setAiQuery(e.target.value)}
                      onKeyDown={handleAIKeyDown}
                      placeholder={
                        aiContextText
                          ? "Wie soll der Text verändert werden?"
                          : "Was soll die KI für dich schreiben?"
                      }
                      disabled={aiLoading}
                      autoFocus
                      className="w-full bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl p-4 text-sm leading-relaxed text-stone-800 dark:text-stone-200 outline-none focus:ring-1 focus:ring-black dark:focus:ring-white transition-all min-h-[120px] resize-none placeholder:text-stone-300 dark:placeholder:text-stone-700 disabled:opacity-50"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setTestangebotFaellig(true);
                      if (!isUnlimited) {
                        setShowAIModal(false);
                        resetAIChat();
                        zeigeAngebotOderPreise();
                      } else {
                        handleAIChat();
                      }
                    }}
                    disabled={!aiQuery.trim() || aiLoading}
                    className="w-full py-3 rounded-full bg-stone-900 dark:bg-white text-white dark:text-black text-xs font-bold uppercase tracking-wide hover:opacity-90 disabled:opacity-30 transition-opacity flex items-center justify-center gap-2"
                  >
                    {aiLoading ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white dark:border-black border-t-transparent rounded-full animate-spin"></div>
                        Generiert...
                      </>
                    ) : (
                      "Generieren"
                    )}
                  </button>
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-2">
                    <label className="text-[10px] uppercase font-bold tracking-widest text-stone-400">
                      KI-Antwort
                    </label>
                    <div className="bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl p-4 text-sm leading-relaxed text-stone-800 dark:text-stone-200 max-h-[300px] overflow-y-auto whitespace-pre-wrap">
                      {aiResponse}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setText(aiResponse.slice(0, MAX_TEXT_LENGTH));
                        if (humanizeEnabled)
                          setOriginalText(aiResponse.slice(0, MAX_TEXT_LENGTH));
                        setShowAIModal(false);
                        resetAIChat();
                      }}
                      className="flex-1 py-3 rounded-full bg-stone-900 dark:bg-white text-white dark:text-black text-xs font-bold uppercase tracking-wide hover:opacity-90 transition-opacity"
                    >
                      Text verwenden
                    </button>
                    <button
                      onClick={() => {
                        setAiContextText(aiResponse);
                        setAiQuery("");
                        setAiResponse("");
                      }}
                      className="flex-1 py-3 rounded-full border border-stone-200 dark:border-stone-800 text-stone-700 dark:text-stone-300 text-xs font-bold uppercase tracking-wide hover:bg-stone-50 dark:hover:bg-stone-900 transition-colors"
                    >
                      Neu generieren
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {showImageGallery && (
        <ImageGalleryModal
          images={generatedImages}
          onClose={() => setShowImageGallery(false)}
        />
      )}

      {/* MÜNZEN-EINGELÖST-FENSTER — die einzige Stelle, die anzeigt, wie
          viele Downloads freigeschaltet wurden (Pixelart + Konfetti) */}
      {redeemedCredits !== null && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div
            className="relative w-full max-w-xs overflow-hidden bg-[#15171c] border-4 border-[#f4b41a] rounded-2xl p-6 text-center"
            style={{ boxShadow: "6px 6px 0 #1a1200" }}
          >
            <PixelConfetti />
            <div className="relative">
              <div className="flex justify-center gap-1.5 mb-3">
                <CoinIcon size={28} />
                <CoinIcon size={28} />
                <CoinIcon size={28} />
              </div>
              <p className="text-[11px] uppercase font-black tracking-widest text-[#f4b41a]">
                Münzen eingelöst!
              </p>
              <p className="text-5xl font-black tracking-tight text-white mt-3 tabular-nums">
                {redeemedCredits}
              </p>
              <p className="mt-1 t-label" style={{ color: ds.color.muted }}>
                {redeemedCredits === 1
                  ? "Download freigeschaltet"
                  : "Downloads freigeschaltet"}
              </p>
              <button
                onClick={() => setRedeemedCredits(null)}
                className="pixel-btn mt-6 w-full py-2.5 text-[11px] font-black uppercase tracking-wider"
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hinweis-Popup: eine kurze Nachricht und ein blauer Knopf darunter.
          Ersetzt die Toasts, die man leicht übersah oder die zu schnell
          wieder verschwanden. Klick daneben oder auf das Kreuz schließt. */}
      {hinweis && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center p-4"
          style={{
            background: ds.scrim,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
          }}
          onClick={() => setHinweis(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-[400px] animate-fade-in"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: ds.color.surface,
              borderRadius: ds.radius.lg,
              padding: "32px 28px 28px",
              fontFamily: ds.font.body,
              color: ds.color.ink,
            }}
          >
            <button
              onClick={() => setHinweis(null)}
              aria-label="Schließen"
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                border: "none",
                background: "transparent",
                color: ds.color.muted,
                cursor: "pointer",
                display: "inline-flex",
                padding: 4,
              }}
            >
              <X size={18} />
            </button>
            <div className="flex flex-col gap-5">
              {hinweis.titel && (
                <h3 style={sx.display(ds.text.h3)}>{hinweis.titel}</h3>
              )}
              <p style={{ fontSize: 16, lineHeight: 1.5, margin: 0 }}>
                {hinweis.text}
              </p>
              <button
                className="sb-btn sb-btn-primary"
                style={{ width: "100%" }}
                autoFocus
                onClick={() => {
                  const aktion = hinweis.aktion;
                  setHinweis(null);
                  if (aktion) aktion();
                }}
              >
                {hinweis.knopf || "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback — mittig, über dem Loader ("Wird erstellt …"), bleibt auch
          nach Ende des Downloads stehen, bis gesendet oder geschlossen wird. */}
      {showFeedback && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center p-4"
          style={{
            background: ds.scrim,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
          }}
        >
          <div
            role="dialog"
            aria-label="Feedback"
            className="relative w-full max-w-[400px] animate-fade-in"
            style={{
              background: ds.color.surface,
              borderRadius: ds.radius.lg,
              padding: "32px 28px 28px",
              fontFamily: ds.font.body,
              color: ds.color.ink,
            }}
          >
            <button
              onClick={() => setShowFeedback(false)}
              aria-label="Schließen"
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                border: "none",
                background: "transparent",
                color: ds.color.muted,
                cursor: "pointer",
                display: "inline-flex",
                padding: 4,
              }}
            >
              <X size={18} />
            </button>
            {!submitted ? (
              <div className="flex flex-col gap-5">
                <h3 style={sx.display(ds.text.h3)}>
                  Wie gefällt dir Schriftbot?
                </h3>
                <div
                  className="flex justify-between py-4"
                  onMouseLeave={() => setHoverRating(0)}
                >
                  {[1, 2, 3, 4, 5].map((star) => {
                    const aktiv = (hoverRating || rating) >= star;
                    return (
                      <button
                        key={star}
                        type="button"
                        aria-label={`${star} von 5 Sternen`}
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          padding: 2,
                          display: "inline-flex",
                          transition: `transform ${ds.dur.fast} ${ds.ease.out}`,
                        }}
                      >
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            d="M12 2.5l2.94 6.1 6.56.9-4.8 4.6 1.2 6.6L12 17.5l-5.9 3.2 1.2-6.6-4.8-4.6 6.56-.9L12 2.5z"
                            fill={aktiv ? ds.color.accent : ds.color.iconGray}
                            stroke={aktiv ? ds.color.accent : ds.color.iconGray}
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                            style={{
                              transition: `fill ${ds.dur.base} ${ds.ease.out}, stroke ${ds.dur.base} ${ds.ease.out}`,
                            }}
                          />
                        </svg>
                      </button>
                    );
                  })}
                </div>
                <textarea
                  style={{
                    ...sx.input,
                    resize: "none",
                    fontSize: 16,
                    padding: "10px 14px",
                  }}
                  placeholder="Deine Meinung (optional)"
                  rows="2"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                />
                <button
                  onClick={sendFeedbackToFirestore}
                  style={{ ...sx.btnPrimary, width: "100%" }}
                >
                  Senden
                </button>
              </div>
            ) : (
              <div className="py-8 text-center">
                <h3 style={sx.display(ds.text.h3)}>Danke für dein Feedback.</h3>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loader */}
      {isGenerating && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{
            background: ds.scrim,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
          }}
        >
          <div
            className="flex flex-col items-center gap-5 px-10 py-8"
            style={{
              background: ds.color.surface,
              borderRadius: ds.radius.lg,
            }}
          >
            <div className="flex gap-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2.5 h-2.5 rounded-full animate-bounce"
                  style={{
                    background: ds.color.accent,
                    animationDelay: `${i * 0.1}s`,
                  }}
                />
              ))}
            </div>
            <span style={sx.sub}>Wird erstellt …</span>
          </div>
        </div>
      )}

      {/* Der Zeichen-Editor. Er ersetzt den alten Weg über PDF-Vorlage,
          E-Mail und externe Schrifterzeugung: gezeichnet wird direkt hier,
          umgewandelt wird im Browser, gespeichert wird in Firestore. */}
      {zeigeSchriftEditor && user && (
        <Suspense fallback={null}>
          <HandschriftEditor
            uid={user.uid}
            onAbbrechen={() => setZeigeSchriftEditor(false)}
            onFertig={({ anzahlZeichen }) => {
              setEigeneSchriftUid(user.uid);
              setZeigeSchriftEditor(false);
              setFont(user.uid);
              showToast(
                `Deine Handschrift ist fertig — ${anzahlZeichen} Zeichen.`,
                "success"
              );
            }}
          />
        </Suspense>
      )}

      {/* Toast — nicht blockierend, blendet sich selbst aus. Vorher war das
          ein Vollbild-Dialog mit der festen Überschrift "Willkommen!", der
          auch über Fehlermeldungen stand. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed left-1/2 bottom-6 z-[400] w-[calc(100%-32px)] max-w-sm -translate-x-1/2"
        >
          <div
            className="flex items-center gap-3 px-5 py-4"
            style={{
              background: ds.color.surface,
              borderRadius: ds.radius.md,
              fontFamily: ds.font.body,
              fontSize: 16,
              lineHeight: 1.5,
              color: ds.color.ink,
            }}
          >
            <span
              aria-hidden
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                flex: "none",
                background:
                  toast.type === "error" ? ds.color.danger : ds.color.success,
              }}
            />
            <span className="flex-1">{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              aria-label="Schließen"
              style={{
                border: "none",
                background: "transparent",
                color: ds.color.muted,
                cursor: "pointer",
                display: "inline-flex",
              }}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {zeigeTestangebot && (
        <TrialOffer
          plan={TRIAL_PLAN}
          onClose={schliesseTestangebot}
          onStart={() => {
            setZeigeTestangebot(false);
            testangebotDanachRef.current = null;
            zaehleTestangebot("trial_offer_clicked");
            handleBuyProcess(TRIAL_PLAN.priceId, { trial: true });
          }}
        />
      )}

      {/* Bestellübersicht — ersetzt das window.confirm vor dem Checkout. */}
      {pendingPlan && (
        <CheckoutConsent
          plan={pendingPlan}
          busy={checkoutBusy}
          onClose={() => {
            setPendingPlan(null);
            setCheckoutBusy(false);
          }}
          onConfirm={({ consent }) =>
            subscribeToPlan(
              pendingPlan.priceId,
              user,
              consent,
              Boolean(pendingPlan.trialDays)
            )
          }
        />
      )}

      {/* Konto löschen — bestätigungspflichtig, weil nicht umkehrbar. */}
      {showDeleteAccount && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center p-4"
          style={{
            background: ds.scrim,
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-md p-8"
            style={{
              background: ds.color.surface,
              borderRadius: ds.radius.lg,
            }}
          >
            <h2
              style={{
                fontFamily: ds.font.display,
                fontSize: ds.text.h3,
                fontWeight: 400,
                lineHeight: ds.leading.tight,
                color: ds.color.ink,
                margin: 0,
              }}
            >
              Konto endgültig löschen?
            </h2>
            <p
              style={{
                fontFamily: ds.font.body,
                fontSize: 16,
                lineHeight: 1.6,
                color: ds.color.muted,
                margin: "12px 0 24px",
              }}
            >
              Dein Konto, dein laufendes Abo und alle Downloads werden gelöscht.
              Das lässt sich nicht rückgängig machen. Wenn du nur das Abo
              beenden willst, reicht eine Kündigung — dein Konto bleibt dann
              bestehen.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDeleteAccount}
                className="sb-btn"
                style={{
                  width: "100%",
                  background: ds.color.dangerTint,
                  color: ds.color.danger,
                }}
              >
                Ja, Konto löschen
              </button>
              <button
                onClick={() => setShowDeleteAccount(false)}
                className="sb-btn sb-btn-secondary"
                style={{ width: "100%" }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- SIDEBAR ---
          Alle Bedienelemente. Vorher trug hier jedes Label
          `text-[11px] uppercase font-bold tracking-widest` — das ist die
          Schreibweise, die eine bezahlte Anwendung wie eine Fanseite
          aussehen lässt. Jetzt: T3 (Outfit 600, 14px, keine Versalien),
          Füllflächen statt Hairlines, Pillen-Buttons, ein Akzent.        */}
      <div
        className="w-full lg:w-[420px] lg:h-screen lg:sticky lg:top-0 flex flex-col relative z-10"
        style={{
          background: ds.color.canvas,
          borderRight: `1px solid ${ds.color.line}`,
        }}
      >
        <div className="relative z-10 flex-1 overflow-y-auto flex flex-col gap-6 p-6 lg:p-8">
          {/* ── Kopf ── */}
          {/* Titelzeile mit Plakette, darunter die Bewertung — bündig an
              derselben linken Kante. */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-3">
              <h1
                style={{
                  fontFamily: ds.font.display,
                  fontWeight: 400,
                  fontSize: 32,
                  lineHeight: ds.leading.tight,
                  letterSpacing: ds.tracking.tight,
                  color: ds.color.ink,
                  margin: 0,
                }}
              >
                Schriftbot.com
              </h1>
              <span
                style={{
                  flex: "none",
                  background: ds.color.accent,
                  color: "#fff",
                  borderRadius: ds.radius.pill,
                  padding: "6px 12px",
                  fontFamily: ds.font.body,
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                110k Nutzer
              </span>
            </div>
            <RatingSummary />
          </div>

          {/* ── Text ── */}
          <div className="flex flex-col gap-3">
            <div className="flex justify-between items-baseline">
              <label style={sx.sub}>Dein Text</label>
              <span
                style={{
                  fontFamily: ds.font.body,
                  fontSize: 14,
                  fontWeight: 500,
                  fontVariantNumeric: "tabular-nums",
                  color:
                    text.length > MAX_TEXT_LENGTH - 100
                      ? ds.color.danger
                      : ds.color.subtle,
                }}
              >
                {text.length} / {MAX_TEXT_LENGTH}
              </span>
            </div>
            <textarea
              className="sb-input"
              style={{ minHeight: 200, resize: "vertical" }}
              value={text}
              onChange={(e) => {
                setText(
                  bereinigeEingabe(e.target.value).slice(0, MAX_TEXT_LENGTH)
                );
              }}
              placeholder="Schreibe hier …"
            />
          </div>

          {/* ── Einstellungen ── */}
          <div className="flex flex-col gap-5">
            <Field label="Schriftart">
              <select
                className="sb-input"
                value={font}
                onChange={handleFontChange}
              >
                <optgroup label="Standard">
                  <option value="Sebastian">Sebastian</option>
                  <option value="Moritz">Moritz</option>
                  <option value="Luis">Luis</option>
                  <option value="Leonie">Leonie</option>
                  <option value="Paula">Paula</option>
                  <option value="Robin">Robin</option>
                </optgroup>
                <optgroup label="Unordentlich">
                  <option value="Alex">Alex</option>
                  <option value="Theo">Theo</option>
                </optgroup>
                <optgroup label="Ordentlich">
                  <option value="Maya">Maya</option>
                  <option value="Lisa">Lisa</option>
                  <option value="Amelie">Amelie</option>
                  <option value="Max">Max</option>
                  <option value="Marie">Marie</option>
                  <option value="Tom">Tom (Dick)</option>
                </optgroup>
                <optgroup label="Extra">
                  <option value="Anton">Anton (Groß)</option>
                  <option value="Druckschrift">Phil (Druckschrift)</option>
                  <option value="Melissa">Melissa (Schreibschrift)</option>
                  <option value="Noah">Noah (GROSSBUCHSTABEN)</option>
                </optgroup>
              </select>

              {/* "Meine Handschrift nutzen" ist der eine Knopf für diesen Fall
                  und ersetzt das frühere Kästchen sowie den zusätzlichen
                  Knopf "Eigene Handschrift hinzufügen" — beide führten am
                  Ende auf denselben Weg.

                  Er steht immer da, auch ohne passendes Abo. Wer ohne Abo
                  klickt, landet in der Preisübersicht statt vor einem Knopf,
                  den es für ihn scheinbar nicht gibt. Wer das Abo hat, aber
                  noch nichts gezeichnet hat, landet im Editor.

                  Die Schrift dahinter kann beides sein: eine im Browser
                  gezeichnete oder eine mitgelieferte TTF von Bestandsnutzern.
                  getFontFamily entscheidet das anhand der uid, hier ist es
                  derselbe Knopf. */}
              {user && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button
                    onClick={handleEigeneHandschriftNutzen}
                    className={`sb-btn ${
                      eigeneHandschriftAktiv
                        ? "sb-btn-success"
                        : "sb-btn-primary"
                    }`}
                    /* Der Text bleibt in einer Zeile: weniger Innenabstand und nowrap, dafür
                       bekommt der Stift-Knopf nur noch seine eigene Höhe als Breite. */
                    style={{
                      flex: 1,
                      minWidth: 0,
                      padding: "0 16px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {eigeneHandschriftAktiv ? (
                      <>
                        <Check size={17} /> Meine Handschrift ist aktiv
                      </>
                    ) : (
                      <>
                        <Signature size={17} /> Meine Handschrift nutzen
                      </>
                    )}
                  </button>

                  {/* Bearbeiten: nur mit dem Abo, das die eigene Handschrift
                      enthält. Bestandsnutzer mit mitgelieferter Schriftdatei
                      sehen den Knopf nicht, deren Schrift bleibt unangetastet. */}
                  {ownHandwritingAllowed &&
                    (!hasCustomHandwriting() ||
                      (eigeneSchriftUid && eigeneSchriftUid === user.uid)) && (
                      <button
                        onClick={() => setZeigeSchriftEditor(true)}
                        className="sb-btn sb-btn-white"
                        aria-label={
                          hasCustomHandwriting()
                            ? "Handschrift bearbeiten"
                            : "Handschrift zeichnen"
                        }
                        title={
                          hasCustomHandwriting()
                            ? "Handschrift bearbeiten"
                            : "Handschrift zeichnen"
                        }
                        style={{ flex: "0 0 auto", width: 50, padding: 0 }}
                      >
                        <PenLine size={17} />
                      </button>
                    )}
                </div>
              )}

              {/* Die Strichstärke gehört zur eigenen Handschrift, deshalb
                  erscheint der Regler nur, solange sie eingestellt ist. */}
              {eigeneHandschriftAktiv && (
                <div style={{ marginTop: 14 }}>
                  <Field
                    label="Strichstärke"
                    value={`${Math.round(strichStaerke * 100)} %`}
                  >
                    <input
                      type="range"
                      className="sb-range"
                      min={0}
                      max={100}
                      value={Math.round(strichStaerke * 100)}
                      onChange={(e) =>
                        setStrichStaerke(Number(e.target.value) / 100)
                      }
                    />
                  </Field>
                </div>
              )}
            </Field>

            <Field label="Wortabstand">
              <select
                className="sb-input"
                value={wordSpacing}
                onChange={(e) => setWordSpacing(e.target.value)}
              >
                <option value="normal">Klein (normal)</option>
                <option value="large">Groß (2 Leerzeichen)</option>
              </select>
            </Field>

            <Field
              label="Schriftgröße"
              value={`${Math.round(fontSizeScale * 100)} %`}
            >
              <input
                type="range"
                className="sb-range"
                min={MIN_FONT_SCALE_PERCENT}
                max={MAX_FONT_SCALE_PERCENT}
                step={FONT_SCALE_STEP_PERCENT}
                value={Math.round(fontSizeScale * 100)}
                onChange={(e) => setFontSizeScale(Number(e.target.value) / 100)}
              />
            </Field>

            <Field label="Unordentlichkeit" value={`${messiness} %`}>
              <input
                type="range"
                className="sb-range"
                min="0"
                max="100"
                value={messiness}
                onChange={(e) => setMessiness(Number(e.target.value))}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Schriftfarbe">
                <div
                  className="flex items-center gap-3"
                  style={{
                    background: ds.color.soft,
                    borderRadius: ds.radius.md,
                    padding: 10,
                  }}
                >
                  <input
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                  />
                  <span
                    style={{
                      fontFamily: ds.font.body,
                      fontSize: 14,
                      fontWeight: 500,
                      color: ds.color.muted,
                      textTransform: "uppercase",
                    }}
                  >
                    {textColor}
                  </span>
                </div>
              </Field>

              <Field label="Papierfarbe">
                <div
                  className="flex items-center gap-3"
                  style={{
                    background: ds.color.soft,
                    borderRadius: ds.radius.md,
                    padding: 10,
                  }}
                >
                  <input
                    type="color"
                    value={paperColor}
                    onChange={(e) => setPaperColor(e.target.value)}
                  />
                  <span
                    style={{
                      fontFamily: ds.font.body,
                      fontSize: 14,
                      fontWeight: 500,
                      color: ds.color.muted,
                      textTransform: "uppercase",
                    }}
                  >
                    {paperColor}
                  </span>
                </div>
              </Field>
            </div>

            <Field label="Lineatur">
              <select
                className="sb-input"
                value={lineStyle}
                onChange={(e) => setLineStyle(e.target.value)}
              >
                <option value="lines">Linien</option>
                <option value="grid">Kariert</option>
                <option value="blank">Blanko</option>
                <optgroup label="GoodNotes-Vorlagen">
                  {Object.entries(GN_PAPIERE).map(([wert, p]) => (
                    <option key={wert} value={wert}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
              </select>
            </Field>
          </div>

          {/* ── Herunterladen: die Hauptaktion, deshalb der einzige
                 farbige Button auf der Seite. ── */}
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <button
                className="sb-btn sb-btn-primary"
                onClick={() => handleDownloadClick("image")}
                disabled={isGenerating || !text}
              >
                <Download size={16} /> Bild
              </button>
              <button
                className="sb-btn sb-btn-primary"
                onClick={() => handleDownloadClick("pdf")}
                disabled={isGenerating || !text}
              >
                <Download size={16} /> PDF
              </button>
              {/* GoodNotes: jeder Buchstabe bleibt ein einzelner Strich und lässt
                  sich radieren. Immer sichtbar, ohne Abo führt der Klick in die
                  Preisübersicht (siehe handleDownloadClick). */}
              <button
                className="sb-btn sb-btn-primary"
                onClick={() => handleDownloadClick("goodnotes")}
                disabled={isGenerating || !text}
                title="Als GoodNotes-Datei, jeder Buchstabe radierbar"
                style={{
                  gridColumn: "1 / -1",
                  padding: "0 18px",
                  whiteSpace: "nowrap",
                }}
              >
                <Download size={16} /> Goodnotes
                {/* Leisere Ergänzung in derselben Zeile */}
                <span
                  style={{
                    fontSize: 12.5,
                    fontWeight: 400,
                    opacity: 0.8,
                    letterSpacing: 0,
                  }}
                >
                  radierbar
                </span>
              </button>
            </div>
            <p style={{ ...sx.caption, lineHeight: 1.5 }}>
              Die Vorschau zeigt das Ergebnis genau so, wie der Download
              aussehen wird — nur ohne Wasserzeichen.
            </p>
          </div>

          {/* Für Abgemeldete ist das DIE Aktion der Seite — also der
              Primärknopf, nicht der leise Sekundärknopf. */}
          {!user && (
            <button
              onClick={handleLogin}
              className="sb-btn sb-btn-primary"
              style={{ width: "100%" }}
            >
              <LogIn size={16} /> Anmelden oder registrieren
            </button>
          )}

          {/* ── Konto ── */}
          {user && (
            <div className="flex flex-col gap-3">
              {!isUnlimited && (
                <div
                  className="flex flex-col gap-4"
                  style={{
                    background: ds.color.surface,
                    borderRadius: ds.radius.lg,
                    padding: 20,
                  }}
                >
                  <div className="flex items-baseline justify-between">
                    <span style={sx.sub}>Verbleibend</span>
                    {hasPREMIUM && <span style={sx.caption}>diesen Monat</span>}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      style={{
                        fontFamily: ds.font.display,
                        fontWeight: 400,
                        fontSize: 40,
                        lineHeight: 1,
                        letterSpacing: ds.tracking.tight,
                        color: ds.color.ink,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {totalCredits}
                    </span>
                    <span style={sx.caption}>Downloads</span>
                  </div>
                  <button
                    onClick={zeigeAngebotOderPreise}
                    className="sb-btn sb-btn-secondary sb-btn-sm"
                    style={{ width: "100%" }}
                  >
                    Mehr Downloads freischalten
                  </button>
                </div>
              )}

              {isUnlimited && (
                <div
                  className="flex flex-col gap-1"
                  style={{
                    background: ds.color.surface,
                    borderRadius: ds.radius.lg,
                    padding: 20,
                  }}
                >
                  <span style={sx.sub}>Dein Tarif</span>
                  <span
                    style={{
                      fontFamily: ds.font.display,
                      fontWeight: 400,
                      fontSize: 32,
                      lineHeight: 1.1,
                      letterSpacing: ds.tracking.tight,
                      color: ds.color.ink,
                    }}
                  >
                    Unlimited
                  </span>
                  <span style={sx.caption}>Unbegrenzte Downloads</span>
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <span
                  style={{
                    ...sx.caption,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user.email}
                </span>
                {hasPREMIUM && (
                  <a
                    href="https://billing.stripe.com/p/login/28EfZaa2N72L74vbuteUU00"
                    target="_blank"
                    rel="noreferrer"
                    className="sb-link"
                    style={{
                      flex: "none",
                      fontFamily: ds.font.body,
                      fontSize: 14,
                      fontWeight: 600,
                      color: ds.color.ink,
                    }}
                  >
                    Abo verwalten
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* --- MAIN PREVIEW AREA ---
          Das Blatt ist hier das Einzige, was leuchtet. Deshalb liegt es
          auf der ruhigen Canvas-Fläche, ohne Punktraster und ohne Rahmen —
          der Kontrast zwischen Papier und Hintergrund trägt die Seite. */}
      <div
        className="flex-1 relative z-10 overflow-y-auto overflow-x-hidden flex flex-col items-center"
        style={{ background: ds.color.canvas }}
      >
        <div className="w-full h-full flex flex-col items-center px-5 py-8 lg:py-12">
          <div
            ref={wrapperRef}
            className="w-full max-w-[794px] flex flex-col"
            style={{ gap: ds.gutter }}
          >
            {(pages.length > 0
              ? pages.map((page, index) => ({
                  key: `${index}-${messiness}-${variationSeed}-${font}`,
                  text: page.text,
                  index,
                  startIndex: page.startIndex,
                }))
              : [
                  {
                    key: `single-${messiness}-${variationSeed}-${font}`,
                    text: text || "",
                    index: 0,
                    startIndex: 0,
                  },
                ]
            ).map((page) => (
              <div key={page.key} className="relative">
                <A4Page
                  pageText={page.text}
                  pageIndex={page.index}
                  startIndex={page.startIndex}
                  messiness={messiness}
                  wordSpacing={wordSpacing}
                  lineHeight={lineHeight}
                />
                {!isUnlimited && (
                  <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
                    <div className="w-full h-full grid grid-cols-3 content-around p-4 opacity-30">
                      {[...Array(24)].map((_, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-center p-4"
                        >
                          <div
                            className="-rotate-45 select-none whitespace-nowrap mix-blend-multiply"
                            style={{
                              fontFamily: ds.font.body,
                              fontWeight: 600,
                              fontSize: "clamp(16px, 2.4vw, 28px)",
                              color: "#B9BCC4",
                            }}
                          >
                            Vorschau
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ── Fuß ── */}
          <footer
            className="w-full max-w-[794px] flex flex-col gap-3"
            style={{ marginTop: 40, paddingBottom: 8 }}
          >
            <div
              className="flex flex-wrap items-center"
              style={{
                gap: "10px 20px",
                fontFamily: ds.font.body,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <Link to="/impressum" target="_blank" className="sb-link">
                Impressum
              </Link>
              <Link to="/datenschutz" target="_blank" className="sb-link">
                Datenschutz
              </Link>
              <Link to="/agb" target="_blank" className="sb-link">
                AGB
              </Link>
              <Link to="/kuendigen" className="sb-link">
                Kündigen
              </Link>
              <Link to="/widerruf" className="sb-link">
                Vertrag widerrufen
              </Link>
              {user && (
                <>
                  <button onClick={handleLogout} className="sb-link">
                    Abmelden
                  </button>
                  <button
                    onClick={() => setShowDeleteAccount(true)}
                    className="sb-link"
                  >
                    Konto löschen
                  </button>
                </>
              )}
            </div>
            <p style={{ ...sx.caption, margin: 0 }}>
              Support: schriftbot@gmail.com · © 2026 schriftbot.com
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// FÜGE DIESE FUNKTION GANZ AM ENDE DEINER DATEI HINZU
// (außerhalb der Haupt-Komponente)
// -----------------------------------------------------------

const ImageGalleryModal = ({ images, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Body-Scroll blockieren
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, []);

  const handleShare = async (imageUrl, index) => {
    try {
      // Data URL zu Blob konvertieren
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], `schriftbot-seite-${index + 1}.png`, {
        type: "image/png",
        lastModified: Date.now(),
      });

      // Prüfen ob Share API verfügbar ist
      if (navigator.share) {
        try {
          await navigator.share({
            files: [file],
            title: `Schriftbot Seite ${index + 1}`,
            text: "Erstellt mit Schriftbot.com",
          });
          return;
        } catch (shareError) {
          // User hat abgebrochen oder Share hat nicht funktioniert
          console.log("Share abgebrochen oder fehlgeschlagen:", shareError);
        }
      }
    } catch (error) {
      console.error("Fehler beim Teilen:", error);
      alert("Fehler beim Teilen. Versuche es mit dem Sichern-Button.");
    }
  };

  const handleDownload = (imageUrl, index) => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `schriftbot-seite-${index + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  // Touch-Swipe für Navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#050505] flex flex-col animate-fade-in"
      style={{ touchAction: "none" }}
    >
      {/* --- HEADER --- */}
      <div className="absolute top-0 left-0 right-0 p-6 flex items-center justify-between z-20 pointer-events-none">
        {/* Page Counter (Minimal Mono) */}
        <div className="bg-black/30 backdrop-blur-md border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
          <span className="text-[10px] font-mono text-stone-300 tracking-widest">
            {currentIndex + 1} / {images.length}
          </span>
        </div>

        {/* Close Button (Clean Icon) */}
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-md border border-white/10 text-white hover:bg-white hover:text-black transition-all duration-300 pointer-events-auto group"
        >
          <X
            size={18}
            className="group-hover:rotate-90 transition-transform duration-300"
          />
        </button>
      </div>

      {/* --- MAIN IMAGE AREA --- */}
      <div
        className="flex-1 flex items-center justify-center relative overflow-hidden w-full h-full"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Navigation Buttons (Desktop - Minimal Circles) */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-6 w-12 h-12 flex items-center justify-center rounded-full border border-white/10 bg-black/20 backdrop-blur-sm text-white hover:bg-white hover:text-black hover:scale-110 transition-all duration-300 z-10 hidden md:flex"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-6 w-12 h-12 flex items-center justify-center rounded-full border border-white/10 bg-black/20 backdrop-blur-sm text-white hover:bg-white hover:text-black hover:scale-110 transition-all duration-300 z-10 hidden md:flex"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {/* Das Bild */}
        <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12 transition-transform duration-300 ease-out">
          <img
            src={images[currentIndex]}
            alt={`Seite ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain shadow-[0_0_50px_rgba(0,0,0,0.5)] select-none"
            draggable="false"
          />
        </div>
      </div>

      {/* --- FOOTER CONTROLS --- */}
      <div className="flex flex-col items-center gap-6 pb-8 z-20 bg-gradient-to-t from-black via-black/80 to-transparent pt-12">
        {/* Action Buttons (Pill Shape) */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleShare(images[currentIndex], currentIndex)}
            className="glass-accent flex items-center gap-2 px-8 py-3 rounded-full text-xs font-bold uppercase tracking-wider"
          >
            <Share2 size={14} />
            Teilen
          </button>

          <button
            onClick={() => handleDownload(images[currentIndex], currentIndex)}
            className="flex items-center gap-2 px-8 py-3 bg-transparent border border-white/20 text-white rounded-full text-xs font-bold uppercase tracking-wider hover:bg-white/10 hover:border-white transition-all active:scale-95"
          >
            <Download size={14} />
            Download
          </button>
        </div>

        {/* Thumbnail Strip (Minimal) */}
        {images.length > 1 && (
          <div
            className="flex gap-3 px-6 overflow-x-auto w-full justify-center max-w-3xl no-scrollbar"
            style={{ maxHeight: "80px" }}
          >
            {images.map((img, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative flex-shrink-0 w-12 h-16 rounded-md overflow-hidden transition-all duration-300 ${
                  index === currentIndex
                    ? "ring-2 ring-white scale-110 opacity-100"
                    : "opacity-40 hover:opacity-80 grayscale"
                }`}
              >
                <img
                  src={img}
                  alt={`Seite ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

const PriceDisplay = ({ price, isPremium }) => {
  const [euro, cent] = price.split(",");
  return (
    <span
      className={` italic leading-none flex items-baseline ${
        isPremium
          ? "text-white dark:text-black"
          : "text-zinc-900 dark:text-white"
      }`}
    >
      <span className="text-md">{euro}</span>
      <span className="text-xs">,{cent}</span>
      <span className="text-sm ml-0.5">€</span>
    </span>
  );
};

function PricingButton({
  title,
  subtitle,
  price,
  period,
  onClick,
  badge,
  highlight,
  isPremium,
  isRecommended,
}) {
  const lines = subtitle.split("\n");
  const mainText = lines[0];
  const features = lines.slice(1);

  return (
    <button
      onClick={onClick}
      className={`group flex justify-between items-center p-6 rounded-xl border transition-all relative overflow-hidden shadow-md w-full text-left active:scale-[0.98]
      ${
        isRecommended
          ? "bg-yellow-100 border-yellow-400 text-yellow-900 hover:bg-yellow-200"
          : isPremium
          ? "bg-zinc-900 dark:bg-white border-zinc-900 dark:border-white text-white dark:text-black hover:opacity-95"
          : "bg-white dark:bg-[#111] border-zinc-200 dark:border-zinc-800 hover:border-zinc-400 dark:hover:border-zinc-600"
      }
    `}
    >
      {isRecommended && (
        <div className="absolute top-0 right-0 bg-yellow-400 text-white text-[10px] font-bold uppercase px-2 py-1 rounded-bl-md">
          Empfohlen
        </div>
      )}

      <div className="flex-1 pr-4">
        <div className="font-bold text-lg">{title}</div>
        <div
          className={`text-sm mt-1 ${
            isPremium
              ? "text-white dark:text-black" // Premium: weiß im Light, schwarz im Dark
              : "text-black dark:text-white" // Normal: schwarz im Light, weiß im Dark
          }`}
        >
          {mainText}
        </div>
        <div className="mt-2 space-y-1">
          {features.map((feature, idx) => {
            const isLastTwo = idx >= features.length - 2;
            const showCheck = isPremium || !isLastTwo; // Haken nur bei Premium oder nicht den letzten 2
            return (
              <div key={idx} className="text-xs flex items-start gap-2">
                <span
                  className={
                    showCheck ? "text-green-500 mt-0.5" : "text-red-600 mt-0.5"
                  }
                >
                  {showCheck ? "✓" : "✗"}
                </span>
                <span className={showCheck ? "text-green-600" : "text-red-600"}>
                  {feature.trim()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-right flex flex-col items-end shrink-0">
        <PriceDisplay price={price} isPremium={isPremium || isRecommended} />
        {period && (
          <div className="text-[10px] uppercase tracking-tighter font-bold mt-1 text-zinc-400">
            {period}
          </div>
        )}
      </div>
    </button>
  );
}
